/**
 * HomeScene.ts
 * 2.5D Isometric Room Scene ("Home").
 * Renders 8x8 floor grid, back walls, dynamic windows, interactive furniture nodes,
 * and animated player character with A* pathfinding navigation and direct touch modals.
 */

import Phaser from '../engine/MicroCanvasEngine';
import { IsometricEngine, GridCoord, IsoPoint } from '../engine/IsometricEngine';
import { Pathfinding } from '../engine/Pathfinding';
import { PixelArtGenerator } from '../engine/PixelArtGenerator';
import { GameState } from '../core/GameState';
import { EventBus } from '../core/EventBus';
import { PlacedFurniture } from '../core/Types';
import { ToastManager } from '../ui/ToastManager';
import { DecorCatalogModal } from '../ui/Modals/DecorCatalogModal';
import { DecorSystem, FURNITURE_CATALOG_ITEMS } from '../systems/DecorSystem';
import {
  getAllSouvenirEntries,
  getSouvenirEntry,
  isSouvenirUnlocked,
  normalizeSouvenirId,
} from '../content/StateSouvenirsRegistry';
import { renderDecorFurnitureVisual, renderSouvenirVisual, renderUiGouacheIcon } from '../ui/Icons';
import {
  cancelPendingScrollRestores,
  preserveScrollPositions,
  preventButtonFocusScroll
} from '../ui/ScrollPreserver';

export class HomeScene extends Phaser.Scene {
  public static readonly MIN_ZOOM = 0.6;
  public static readonly MAX_ZOOM = 2.6;
  public static readonly DEFAULT_ZOOM = 1.35;
  public static readonly BADGE_AUTO_HIDE_MS = 3000;
  public static readonly BADGE_FADE_OUT_MS = 300;
  public static readonly STAT_ALERT_THRESHOLD = 20;

  /**
   * Grid axis along which each 2-cell PNG illustration is painted when `flipX = false`.
   * `row` = long side runs North-East → South-West (footprint 1x2, rotation 90);
   * `col` = long side runs North-West → South-East (footprint 2x1, rotation 0).
   */
  private static readonly FURNITURE_SPRITE_NATIVE_AXIS: Readonly<Record<string, 'col' | 'row'>> = {
    bed_t1: 'row',
    bed_t2: 'row',
    bed_t3: 'row',
    sofa_t1: 'col',
    sofa_t2: 'row',
    sofa_t3: 'row',
    desk_t1: 'col',
    desk_t2: 'row',
    desk_t3: 'row',
  };

  /**
   * Point of each PNG illustration — `[x, y]` as fractions (0 = left/top, 1 = right/bottom) of its
   * content bbox — pinned onto the footprint anchor (`getFurnitureFootprintCenter` + `getFurnitureVisualOffset`).
   * Starts from the floor-contact center measured on the PNG alpha masks, then is moved by the smallest
   * amount that keeps every opaque pixel ≥ 1px inside the diamond's two front (lower) edges and its
   * left/right vertices. The art is not drawn in a strict 2:1 projection and its front corner is rarely
   * centered, so the raw floor center lets 2-cell pieces poke 2.5–6.4px into the cells in front.
   */
  private static readonly FURNITURE_FLOOR_ANCHOR: Readonly<Record<string, readonly [number, number]>> = {
    bed_t1: [0.516, 0.649],
    bed_t2: [0.495, 0.774],
    bed_t3: [0.497, 0.742],
    desk_t1: [0.536, 0.922],
    desk_t2: [0.48, 0.868],
    desk_t3: [0.495, 0.863],
    sofa_t1: [0.46, 0.92],
    sofa_t2: [0.608, 0.902],
    sofa_t3: [0.546, 0.785],
    fridge_t1: [0.699, 1.024],
    fridge_t2: [0.666, 1.007],
    fridge_t3: [0.579, 0.96],
    plant_cactus: [0.5, 0.975],
    plant_monstera: [0.512, 0.972],
    plant_bonsai: [0.507, 0.979],
    souvenir_ny_statue_liberty: [0.5, 0.9],
    souvenir_ca_golden_gate: [0.5, 0.87],
    souvenir_tx_longhorn: [0.5, 0.84],
    souvenir_fl_rocket_flamingo: [0.5, 0.89],
    souvenir_wa_space_needle: [0.5, 0.88],
    souvenir_az_grand_canyon: [0.5, 0.86],
    souvenir_or_crater_lake: [0.5, 0.86],
    souvenir_id_star_garnet: [0.5, 0.86],
    souvenir_nv_hoover_dam: [0.5, 0.86],
    souvenir_co_rocky_peaks: [0.5, 0.86],
    souvenir_mo_gateway_arch: [0.5, 0.86],
    souvenir_hi_aloha_volcano: [0.5, 0.86],
    souvenir_ak_aurora_totem: [0.5, 0.86],
    souvenir_dc_capitol_dome: [0.5, 0.86],
    souvenir_forest_smoky_mill: [0.5, 0.86],
    desk_chair_cozy: [0.298, 0.875],
  };
  private static readonly DEFAULT_FLOOR_ANCHOR: readonly [number, number] = [0.5, 0.85];
  /** Desk chair stays ~62% of the desk's scale so it reads as a chair, not a second desk. */
  private static readonly DESK_CHAIR_SCALE_RATIO = 0.62;
  /**
   * Chair feet offset from the desk anchor toward its front edge (x mirrors for 1x2 desks):
   * centers the chair into the desk kneehole while keeping every chair pixel strictly inside the 2-cell diamond.
   */
  private static readonly DESK_CHAIR_OFFSET_X = 8;
  private static readonly DESK_CHAIR_OFFSET_Y = 0;
  /**
   * Per-tier `[longMin, longMax, shortMin, shortMax]` grid-cell bounds wrapping the full wooden foot soles
   * of all 4 desk legs + chair legs with equal symmetric padding across 0° (2x1) and 90° (1x2).
   */
  private static readonly DESK_SHADOW_GRID_BOUNDS: Readonly<
    Record<number, readonly [number, number, number, number]>
  > = {
    1: [0.493, 1.508, 0.099, 0.968],
    2: [0.379, 1.621, 0.067, 0.99],
    3: [0.265, 1.736, -0.025, 0.992],
  };
  /**
   * Per-tier `[longMin, longMax, shortMin, shortMax]` grid-cell bounds wrapping the 2-cell bed frame
   * and canopy posts aligned to the `0.099` wall-back standard line (`col`/`row > 0.06` baseboard).
   */
  private static readonly BED_SHADOW_GRID_BOUNDS: Readonly<
    Record<number, readonly [number, number, number, number]>
  > = {
    1: [0.675, 1.355, 0.099, 0.915],
    2: [0.685, 1.315, 0.099, 0.91],
    3: [0.575, 1.445, 0.099, 0.925],
  };
  /**
   * Rectangular `[longMin, longMax, shortMin, shortMax]` grid-cell bounds for 2-cell sofas (`sofa_t2` 4-leg
   * loveseat and `sofa_t3` L-chaise + area rug), aligned to the `0.099` wall-back standard line.
   */
  private static readonly SOFA_RECT_SHADOW_GRID_BOUNDS: Readonly<
    Record<2 | 3, readonly [number, number, number, number]>
  > = {
    2: [0.248, 1.752, 0.099, 0.535],
    3: [0.385, 1.635, 0.099, 0.925],
  };
  /**
   * Per-tier `[backMin, frontMax, sideMin, sideMax]` 1x1 grid-cell bounds for upright fridges
   * (`rotation = 0` backs onto Left Wall `col = 0`; `rotation = 90` swaps axes onto Right Wall `row = 0`).
   */
  private static readonly FRIDGE_SHADOW_GRID_BOUNDS: Readonly<
    Record<number, readonly [number, number, number, number]>
  > = {
    1: [0.099, 0.528, 0.155, 0.845],
    2: [0.099, 0.55, 0.138, 0.862],
    3: [0.099, 0.672, 0.08, 0.92],
  };
  /** `[backMin, frontMax, sideMin, sideMax]` 1x1 grid-cell bounds for `plant_bonsai`'s 4-leg wooden stand. */
  private static readonly BONSAI_SHADOW_GRID_BOUNDS: readonly [number, number, number, number] = [
    0.288, 0.698, 0.272, 0.738,
  ];
  /**
   * 2:1 isometric oval contact shadow parameters (screen-space offsets from `(sx, sy)` and radii in px)
   * for round-base furniture (`plant_cactus`, `plant_monstera`, and `sofa_t1`'s round woven rug + pouf).
   */
  private static readonly ROUND_BASE_OVAL_SHADOWS: Readonly<
    Record<
      string,
      {
        readonly cxOffset: number;
        readonly cyOffset: number;
        readonly rx: number;
        readonly ry: number;
        readonly tiltRad: number;
      }
    >
  > = {
    plant_cactus: { cxOffset: 0, cyOffset: -2.0, rx: 6.8, ry: 3.4, tiltRad: 0 },
    plant_monstera: { cxOffset: 0, cyOffset: -1.6, rx: 6.8, ry: 3.4, tiltRad: 0 },
    sofa_t1: { cxOffset: 1.2, cyOffset: -1.8, rx: 17.2, ry: 5.6, tiltRad: 0.12 },
  };
  private static readonly PLAYER_COZY_BASE_SCALE = 1.10;
  private static readonly PLAYER_COZY_FOOT_ORIGIN_Y = 0.995;
  private static readonly PLAYER_COZY_FOOT_GROUND_OFFSET_Y = 2;
  private static readonly PLAYER_SHADOW_GROUND_OFFSET_Y = 0;
  private static readonly PLAYER_SHADOW_BASE_SCALE_X = 1.68;
  private static readonly PLAYER_SHADOW_ASPECT_Y = 0.8;
  /** Minimum half-width (px) of a furniture body tap target so slim 1x1 pieces stay easy to hit. */
  private static readonly MIN_FURNITURE_TOUCH_HALF_WIDTH = 16;

  public floorSprites: Phaser.GameObjects.Image[] = [];
  private pedestalGraphics: Phaser.GameObjects.Graphics | null = null;
  private wallArchitectureGraphics: Phaser.GameObjects.Graphics | null = null;
  private wallDecorGraphics: Phaser.GameObjects.Graphics | null = null;
  private roomZonesGraphics: Phaser.GameObjects.Graphics | null = null;
  private roomPropsGraphics: Phaser.GameObjects.Graphics | null = null;
  private furnitureShadowGraphics: Phaser.GameObjects.Graphics | null = null;
  private furnitureEffectsGraphics: Phaser.GameObjects.Graphics | null = null;
  private windowLightGraphics: Phaser.GameObjects.Graphics | null = null;
  private atmosphereGraphics: Phaser.GameObjects.Graphics | null = null;
  private playerRigGraphics: Phaser.GameObjects.Graphics | null = null;
  private playerFxGraphics: Phaser.GameObjects.Graphics | null = null;
  private atmospherePhase: 'day' | 'sunset' | 'night' = 'day';
  private atmosphereTimeSec: number = 0;
  private walkCyclePhase: number = 0;
  private wasCharacterMoving: boolean = false;
  private characterSettleDisplacement: number = 0;
  private characterSettleVelocity: number = 0;
  private lastFootStrikeStep: number = -1;
  private footstepDustPuffs: Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    ageMs: number;
    ttlMs: number;
    radius: number;
  }> = [];
  private activeActivityType: 'none' | 'sleep' | 'work' | 'eat' | 'chill' = 'none';
  private activeActivityRemainingMs: number = 0;
  private ambientMotes: Array<{
    baseX: number;
    baseY: number;
    ampX: number;
    ampY: number;
    freq: number;
    phase: number;
    radius: number;
    inSunbeam: boolean;
  }> = [];
  private gridOverlayGraphics: Phaser.GameObjects.Graphics | null = null;
  private decorBannerContainer: Phaser.GameObjects.Container | null = null;
  public roomShellSprite: Phaser.GameObjects.Image | null = null;
  public furnitureSprites: Map<string, Phaser.GameObjects.Sprite | Phaser.GameObjects.Image> = new Map();
  public deskChairSprites: Map<string, Phaser.GameObjects.Image> = new Map();
  public furnitureBadgeContainers: Map<string, Phaser.GameObjects.Container> = new Map();
  public fadingBadgeContainers: Set<Phaser.GameObjects.Container> = new Set();
  private badgeAutoHideRemainingMs: number = HomeScene.BADGE_AUTO_HIDE_MS;
  private badgesAutoHidden: boolean = false;
  private initialIntroBadgesDone: boolean = false;
  private suppressedUntilLowStats: { energy: boolean; hunger: boolean; happiness: boolean } = {
    energy: false,
    hunger: false,
    happiness: false,
  };
  private lowAlertShownForCurrentDrop: { energy: boolean; hunger: boolean; happiness: boolean } = {
    energy: false,
    hunger: false,
    happiness: false,
  };
  private lastStatSnapshot: { energy: number; hunger: number; happiness: number } = {
    energy: 80,
    hunger: 80,
    happiness: 80,
  };
  public playerSprite!: Phaser.GameObjects.Sprite;
  public playerCozySprite: Phaser.GameObjects.Image | null = null;
  public shadowSprite!: Phaser.GameObjects.Image;
  public targetCursor!: Phaser.GameObjects.Image;
  public windowSprite!: Phaser.GameObjects.Image;
  private cameraFocusTarget: { x: number; y: number } = { x: 195, y: 274 };

  public playerPos: GridCoord = { col: 3, row: 3 };
  public isMoving: boolean = false;
  public isDecorMode: boolean = false;
  private focusedDecorFurnitureId: string | null = null;
  private selectedFurnitureForMoveId: string | null = null;
  private decorActionMenuContainer: Phaser.GameObjects.Container | null = null;
  private isDraggingFurniture: boolean = false;
  private furnitureDragMoved: boolean = false;
  private dragStartGridCol: number = -1;
  private dragStartGridRow: number = -1;
  private wasFurnitureSelectedBeforePointerDown: boolean = false;
  public currentDirection: 'se' | 'sw' | 'ne' | 'nw' = 'se';
  private moveTween: Phaser.Tweens.Tween | null = null;
  private blockedWiggleTween: Phaser.Tweens.Tween | null = null;
  private activePath: GridCoord[] = [];
  private activeWalkRequestId: number = 0;
  private currentStepTile: GridCoord | null = null;
  private unsubscribes: Array<() => void> = [];

  // Input & Camera Pan/Zoom state
  private lastTapActionTimestamp: number = 0;
  private pointerDownOnCanvas: boolean = false;
  private isDraggingCamera: boolean = false;
  private pointerDownScreenX: number = 0;
  private pointerDownScreenY: number = 0;
  private cameraScrollStartX: number = 0;
  private cameraScrollStartY: number = 0;
  private pinchStartDist: number = 0;
  private pinchStartZoom: number = HomeScene.DEFAULT_ZOOM;

  constructor() {
    super({ key: 'HomeScene' });
  }

  public get placedFurnitureSprites(): Map<string, Phaser.GameObjects.Sprite | Phaser.GameObjects.Image> {
    return this.furnitureSprites;
  }

  public get floorTiles(): Phaser.GameObjects.Image[] {
    return this.floorSprites;
  }

  public set floorTiles(tiles: Phaser.GameObjects.Image[]) {
    this.floorSprites = tiles;
  }

  public get playerGridPos(): GridCoord {
    return this.playerPos;
  }

  public set playerGridPos(pos: GridCoord) {
    this.playerPos = pos;
  }

  public refreshFurnitureSprites(): void {
    this.renderFurniture();
  }

  /**
   * Phaser Editor v5 scene hierarchy initializer (`src/scenes/HomeScene.scene`).
   */
  public editorCreate(): void {
    // 0. Render 3D Isometric Diorama Pedestal, Ambient Phase Glow & Thick Foundation Slab
    this.createDioramaPedestal();

    // 1. Render tall 2.5x isometric walls, dynamic 3-phase window, right wall decor & fairy lights
    this.createWalls();

    // 2. Render 8x8 isometric wood floor grid
    this.createFloorGrid();

    // 2.5. Initialize deterministic atmospheric particle field (dust motes / sunset embers / night fireflies)
    this.createAlgorithmicAtmosphere();

    // 3. Render placed furniture, contact shadows, Tier 2/3 visual upgrades, State Souvenir pedestals & action pills
    this.renderFurniture();

    // 4. Create target selection cursor (hidden by default)
    this.targetCursor = this.add.image(0, 0, 'tile_cursor')
      .setOrigin(0.5, 0)
      .setDepth(IsometricEngine.calculateDepth(7, 7, 50))
      .setVisible(false);

    // 5. Spawn 4-directional animated Chibi character + Cozy sprite + dynamic rig overlay
    this.createPlayer();
  }

  public create(): void {
    EventBus.emit('SCENE_CHANGED', 'HomeScene');
    PixelArtGenerator.registerCozyDioramaAssets(this);
    this.upgradeTier3FurnitureTextures();

    // Reset scene-lifecycle references so restarting HomeScene after MapScene never reuses dead Graphics/Sprites
    this.floorSprites = [];
    this.roomShellSprite = null;
    this.playerCozySprite = null;
    this.furnitureSprites.clear();
    this.deskChairSprites.clear();
    this.pedestalGraphics = null;
    this.wallArchitectureGraphics = null;
    this.wallDecorGraphics = null;
    this.roomZonesGraphics = null;
    this.roomPropsGraphics = null;
    this.furnitureShadowGraphics = null;
    this.furnitureEffectsGraphics = null;
    this.windowLightGraphics = null;
    this.atmosphereGraphics = null;
    this.playerRigGraphics = null;
    this.playerFxGraphics = null;
    this.gridOverlayGraphics = null;
    this.decorBannerContainer = null;
    this.decorActionMenuContainer = null;
    this.isMoving = false;
    this.moveTween = null;
    this.blockedWiggleTween = null;
    this.activePath = [];
    this.currentStepTile = null;
    this.walkCyclePhase = 0;
    this.wasCharacterMoving = false;
    this.characterSettleDisplacement = 0;
    this.characterSettleVelocity = 0;
    this.lastFootStrikeStep = -1;
    this.footstepDustPuffs = [];
    this.activeActivityType = 'none';
    this.activeActivityRemainingMs = 0;
    this.isDraggingFurniture = false;
    this.furnitureDragMoved = false;
    this.isDraggingCamera = false;
    this.pointerDownOnCanvas = false;
    this.fadingBadgeContainers.clear();

    const initStats = GameState.getState()?.stats;
    this.badgeAutoHideRemainingMs = HomeScene.BADGE_AUTO_HIDE_MS;
    this.badgesAutoHidden = false;
    this.initialIntroBadgesDone = false;
    this.suppressedUntilLowStats = {
      energy: initStats ? initStats.energy >= 100 : false,
      hunger: initStats ? initStats.hunger >= 100 : false,
      happiness: initStats ? initStats.happiness >= 100 : false,
    };
    this.lowAlertShownForCurrentDrop = {
      energy: false,
      hunger: false,
      happiness: false,
    };
    this.lastStatSnapshot = {
      energy: initStats?.energy ?? 80,
      hunger: initStats?.hunger ?? 80,
      happiness: initStats?.happiness ?? 80,
    };
    this.syncStatBadgeGates();

    // Build Phaser Editor v5 scene layers
    this.editorCreate();

    // 6. Setup 1.35x camera framing, zoom, and balanced room follow
    this.setupCamera();

    // 7. Setup touch, pinch-zoom, wheel-zoom, drag-pan & pointer input raycasting
    this.setupInputHandlers();

    // 8. Listen to external GameState events & mount Cozy 5-State Shelf Bar in lower space
    this.setupEventListeners();
    this.renderHomeCozyShelfBar();
  }

  /**
   * Standard Phaser 4 Scene update lifecycle method.
   * Drives atmospheric particles, 3-phase lighting pulse, 4-directional character stride/breathing,
   * contextual furniture activity animations, and badge auto-hide timers.
   */
  public update(_time: number, delta: number): void {
    const numericDelta = typeof delta === 'number' && Number.isFinite(delta) ? delta : 16.67;
    const rawDelta = Math.max(numericDelta, 0);
    const safeDelta = Math.min(rawDelta, 250);
    this.atmosphereTimeSec += safeDelta / 1000;
    this.renderAtmosphericParticles(this.atmosphereTimeSec);
    this.updateCharacterAnimationFrame(this.atmosphereTimeSec, safeDelta, rawDelta);
    this.renderDynamicFurnitureEffects(this.atmosphereTimeSec);
    this.tickBadgeAutoHide(rawDelta);
  }

  public override _update(deltaMs: number): void {
    super._update(deltaMs);
    this.update(this.atmosphereTimeSec * 1000, deltaMs);
  }

  /**
   * Enhances Tier 3 furniture textures (`bed_t3`, `fridge_t3`, `desk_t3`, `sofa_t3`)
   * with distinct Royal / Artisan details so Tier 3 never shares an identical visual with Tier 2.
   */
  private upgradeTier3FurnitureTextures(): void {
    const tier3Upgrades: Array<{
      key: string;
      decorate: (ctx: CanvasRenderingContext2D, w: number, h: number) => void;
    }> = [
      {
        key: 'bed_t3',
        decorate: (ctx, w, h) => {
          // Royal golden headboard crest & warm quilted canopy trim
          ctx.save();
          ctx.strokeStyle = '#3D251E';
          ctx.lineWidth = Math.max(1.5, w * 0.012);
          ctx.fillStyle = '#F59E0B';
          ctx.beginPath();
          ctx.arc(w * 0.36, h * 0.22, w * 0.045, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle = '#FDE68A';
          ctx.beginPath();
          ctx.arc(w * 0.36, h * 0.22, w * 0.022, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        },
      },
      {
        key: 'fridge_t3',
        decorate: (ctx, w, h) => {
          // Smart Chef touchscreen panel & brushed gold dispenser badge
          ctx.save();
          ctx.fillStyle = '#0F172A';
          ctx.strokeStyle = '#F59E0B';
          ctx.lineWidth = Math.max(1.2, w * 0.014);
          ctx.fillRect(w * 0.52, h * 0.28, w * 0.14, h * 0.16);
          ctx.strokeRect(w * 0.52, h * 0.28, w * 0.14, h * 0.16);
          ctx.fillStyle = '#38BDF8';
          ctx.fillRect(w * 0.54, h * 0.31, w * 0.10, h * 0.05);
          ctx.fillStyle = '#10B981';
          ctx.fillRect(w * 0.54, h * 0.38, w * 0.07, h * 0.03);
          ctx.restore();
        },
      },
      {
        key: 'desk_t3',
        decorate: (ctx, w, h) => {
          // Executive gold studio monitor rim & glowing cyan-gold dual-screen status bar
          ctx.save();
          ctx.strokeStyle = '#F59E0B';
          ctx.lineWidth = Math.max(1.5, w * 0.013);
          ctx.strokeRect(w * 0.34, h * 0.20, w * 0.30, h * 0.18);
          ctx.fillStyle = 'rgba(56, 189, 248, 0.35)';
          ctx.fillRect(w * 0.35, h * 0.21, w * 0.28, h * 0.16);
          ctx.restore();
        },
      },
      {
        key: 'sofa_t3',
        decorate: (ctx, w, h) => {
          // Royal velvet gold-trimmed bolster cushion emblem
          ctx.save();
          ctx.fillStyle = '#F59E0B';
          ctx.strokeStyle = '#3D251E';
          ctx.lineWidth = Math.max(1.2, w * 0.012);
          ctx.beginPath();
          ctx.arc(w * 0.48, h * 0.46, w * 0.042, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
          ctx.restore();
        },
      },
    ];

    for (const item of tier3Upgrades) {
      if (!this.textures.exists(item.key)) continue;
      const tex = this.textures.get(item.key);
      if (!tex || (tex as unknown as { _t3Decorated?: boolean })._t3Decorated) continue;
      const targetCanvas = tex.hdCanvas || tex.canvas;
      if (!targetCanvas || typeof targetCanvas.getContext !== 'function') continue;
      const ctx = targetCanvas.getContext('2d');
      if (ctx) {
        item.decorate(ctx, targetCanvas.width, targetCanvas.height);
        if (tex.canvas && tex.canvas !== targetCanvas && typeof tex.canvas.getContext === 'function') {
          const baseCtx = tex.canvas.getContext('2d');
          if (baseCtx) {
            item.decorate(baseCtx, tex.canvas.width, tex.canvas.height);
          }
        }
        (tex as unknown as { _t3Decorated?: boolean })._t3Decorated = true;
      }
    }
  }

  // =========================================================================
  // ROOM RENDERING (Diorama Pedestal, 2.5x Tall Walls, Dynamic 3-Phase Window)
  // =========================================================================

  /**
   * Renders phase-aware ambient glow around the hand-drawn isometric room diorama
   * and a soft drop-shadow cushion beneath the 3D wooden cutaway foundation.
   */
  private createDioramaPedestal(): void {
    if (this.pedestalGraphics) {
      this.pedestalGraphics.destroy();
    }
    const g = this.add.graphics().setDepth(-25000);
    this.pedestalGraphics = g;
    this.renderPedestalByPhase();
  }

  private renderPedestalByPhase(): void {
    if (!this.pedestalGraphics) return;
    const g = this.pedestalGraphics;
    g.clear();

    const outerHaloColor =
      this.atmospherePhase === 'day'
        ? 0x38bdf8
        : this.atmospherePhase === 'sunset'
        ? 0xf97316
        : 0x312e81;
    const outerHaloAlpha =
      this.atmospherePhase === 'day'
        ? 0.12
        : this.atmospherePhase === 'sunset'
        ? 0.16
        : 0.16;

    const innerWarmColor =
      this.atmospherePhase === 'day'
        ? 0xfef08a
        : this.atmospherePhase === 'sunset'
        ? 0xfb923c
        : 0xf59e0b;
    const innerWarmAlpha =
      this.atmospherePhase === 'day'
        ? 0.09
        : this.atmospherePhase === 'sunset'
        ? 0.10
        : 0.06;

    // Phase-reactive soft radial ambient halo behind the hand-drawn room diorama (no protruding corner triangles)
    g.fillStyle(outerHaloColor, outerHaloAlpha);
    g.fillCircle(195, 265, 205);

    g.fillStyle(innerWarmColor, innerWarmAlpha);
    g.fillCircle(195, 265, 178);

    // Tight drop-shadow cushion beneath the hand-drawn wooden diorama base cutaway
    g.fillStyle(0x020617, 0.54);
    g.beginPath();
    g.moveTo(32, 336);
    g.lineTo(195, 434);
    g.lineTo(358, 336);
    g.lineTo(195, 256);
    g.closePath();
    g.fillPath();
  }

  private createWalls(): void {
    // Cohesive Hand-Drawn Isometric Room Diorama Shell (Good Pizza Great Pizza & Unpacking art style, #3D251E ink outlines)
    // Logical canvas is 256x256 (1024x1024 HD); inner wooden floor diamond top apex (col=0, row=0) is at (0.5, 486/1024) -> (195, 238)
    // Scaled (420/256, 404/256) so the wooden floor inner diamond wraps the 320x160 8x8 grid with zero overflow.
    if (this.roomShellSprite) {
      this.roomShellSprite.destroy();
    }
    this.roomShellSprite = this.add.image(195, 238, 'cozy_room_shell')
      .setOrigin(0.5, 486 / 1024)
      .setScale(420 / 256, 404 / 256)
      .setDepth(-21000);

    this.wallArchitectureGraphics = this.add.graphics().setDepth(-9850);
    this.renderTallWallArchitecture();

    // Keep windowSprite reference synced with phase texture key while rendering crisp 30° isometric window pane in wallDecorGraphics
    this.windowSprite = this.add.image(108.5, 191.5, 'window_day')
      .setOrigin(0.5, 0.5)
      .setScale(1.0)
      .setDepth(-19500)
      .setAlpha(0);

    this.wallDecorGraphics = this.add.graphics().setDepth(-9400);
    this.renderWallDecorations();

    this.windowLightGraphics = this.add.graphics().setDepth(-15000);
    this.updateWindowLighting();
  }

  private renderTallWallArchitecture(): void {
    if (!this.wallArchitectureGraphics) return;
    this.wallArchitectureGraphics.clear();
  }

  private renderWallDecorations(): void {
    if (!this.wallDecorGraphics) return;
    const g = this.wallDecorGraphics;
    g.clear();

    // 1. Dynamic 3-Phase 30° Isometric Window Pane & Wooden Cross-Mullions aligned to cozy_room_shell.png Left Wall Window
    // Glass parallelogram: Top-Left (80, 170.5) -> Top-Right (137, 146) -> Bottom-Right (137, 212) -> Bottom-Left (80, 237)
    if (this.atmospherePhase === 'day') {
      // Bright daytime blue sky (completely covers the baked-in night sky & crescent moon on cozy_room_shell.png)
      g.fillStyle(0x38bdf8, 1.0);
      g.beginPath();
      g.moveTo(80, 170.5);
      g.lineTo(137, 146);
      g.lineTo(137, 184);
      g.lineTo(80, 208.5);
      g.closePath();
      g.fillPath();

      g.fillStyle(0x7dd3fc, 1.0);
      g.beginPath();
      g.moveTo(80, 208.5);
      g.lineTo(137, 184);
      g.lineTo(137, 212);
      g.lineTo(80, 237);
      g.closePath();
      g.fillPath();

      // Sunlit horizon glow along bottom window panes
      g.fillStyle(0xe0f2fe, 0.92);
      g.beginPath();
      g.moveTo(80, 223);
      g.lineTo(137, 198.5);
      g.lineTo(137, 212);
      g.lineTo(80, 237);
      g.closePath();
      g.fillPath();

      // Warm Daytime Golden Sun + Halo in the upper-right pane
      g.fillStyle(0xfef08a, 0.55);
      g.fillCircle(122, 168, 9.5);
      g.fillStyle(0xfde047, 1.0);
      g.fillCircle(122, 168, 5.8);
      g.fillStyle(0xffffff, 0.9);
      g.fillCircle(121, 167, 2.8);

      // Soft Fluffy White Daytime Clouds
      g.fillStyle(0xffffff, 0.92);
      g.fillCircle(92, 188, 4.2);
      g.fillCircle(97, 185, 5.0);
      g.fillCircle(102, 186, 3.8);

      g.fillCircle(118, 197, 3.8);
      g.fillCircle(123, 194, 4.6);
      g.fillCircle(128, 195, 3.4);
    } else if (this.atmospherePhase === 'sunset') {
      // Warm Golden-Hour Sunset Sky
      g.fillStyle(0x9d174d, 1.0);
      g.beginPath();
      g.moveTo(80, 170.5);
      g.lineTo(137, 146);
      g.lineTo(137, 176);
      g.lineTo(80, 200.5);
      g.closePath();
      g.fillPath();

      g.fillStyle(0xf97316, 1.0);
      g.beginPath();
      g.moveTo(80, 200.5);
      g.lineTo(137, 176);
      g.lineTo(137, 198);
      g.lineTo(80, 222.5);
      g.closePath();
      g.fillPath();

      g.fillStyle(0xfde047, 1.0);
      g.beginPath();
      g.moveTo(80, 222.5);
      g.lineTo(137, 198);
      g.lineTo(137, 212);
      g.lineTo(80, 237);
      g.closePath();
      g.fillPath();

      // Setting Golden-Amber Sun
      g.fillStyle(0xfef08a, 0.5);
      g.fillCircle(120, 176, 9.0);
      g.fillStyle(0xfef08a, 1.0);
      g.fillCircle(120, 176, 5.2);
    } else {
      // Starry Night Sky + Glowing Crescent Moon
      g.fillStyle(0x1e1b4b, 1.0);
      g.beginPath();
      g.moveTo(80, 170.5);
      g.lineTo(137, 146);
      g.lineTo(137, 188);
      g.lineTo(80, 212.5);
      g.closePath();
      g.fillPath();

      g.fillStyle(0x312e81, 1.0);
      g.beginPath();
      g.moveTo(80, 212.5);
      g.lineTo(137, 188);
      g.lineTo(137, 212);
      g.lineTo(80, 237);
      g.closePath();
      g.fillPath();

      // Twinkling stars
      g.fillStyle(0xffffff, 0.9);
      g.fillCircle(91, 182, 1.1);
      g.fillCircle(101, 173, 1.3);
      g.fillCircle(95, 214, 1.1);
      g.fillCircle(124, 196, 1.2);

      // Crescent Moon in upper-right pane
      g.fillStyle(0xfef08a, 0.32);
      g.fillCircle(122, 168, 8.2);
      g.fillStyle(0xfde047, 1.0);
      g.fillCircle(122, 168, 5.2);
      g.fillStyle(0x1e1b4b, 1.0);
      g.fillCircle(120, 166.5, 4.4);
    }

    // Diagonal glass specular sheen
    g.fillStyle(0xffffff, 0.16);
    g.beginPath();
    g.moveTo(88, 167);
    g.lineTo(96, 163.5);
    g.lineTo(89, 233);
    g.lineTo(81, 236.5);
    g.closePath();
    g.fillPath();

    // Hand-drawn wooden cross-mullions & inner frame border (#8C5230 warm oak + #3D251E espresso outline)
    // Vertical mullion bar at x = 109.5
    g.fillStyle(0x8c5230, 1.0);
    g.beginPath();
    g.moveTo(106.5, 159.2);
    g.lineTo(112.5, 156.6);
    g.lineTo(112.5, 222.8);
    g.lineTo(106.5, 225.4);
    g.closePath();
    g.fillPath();
    g.lineStyle(1.5, 0x3d251e, 0.98);
    g.strokePath();

    // Tilted 30° horizontal mullion bar from (80, 204.5) to (137, 179.5)
    g.fillStyle(0x8c5230, 1.0);
    g.beginPath();
    g.moveTo(80, 201.5);
    g.lineTo(137, 176.5);
    g.lineTo(137, 182.5);
    g.lineTo(80, 207.5);
    g.closePath();
    g.fillPath();
    g.lineStyle(1.5, 0x3d251e, 0.98);
    g.strokePath();

    // Crisp inner glass frame outline matching #3D251E hand-drawn linework
    g.lineStyle(2.2, 0x3d251e, 0.98);
    g.beginPath();
    g.moveTo(80, 170.5);
    g.lineTo(137, 146);
    g.lineTo(137, 212);
    g.lineTo(80, 237);
    g.closePath();
    g.strokePath();

    // 2. Subtle warm fairy-light bulb glow aligned to the string lights on cozy_room_shell.png
    const bulbAlpha =
      this.atmospherePhase === 'night'
        ? 0.28
        : this.atmospherePhase === 'sunset'
        ? 0.20
        : 0.10;
    const bulbCoords = [
      { x: 68, y: 161 },
      { x: 99, y: 137 },
      { x: 138, y: 121 },
      { x: 172, y: 118 },
      { x: 195, y: 99 },
      { x: 218, y: 118 },
      { x: 251, y: 121 },
      { x: 290, y: 137 },
      { x: 322, y: 161 },
    ];
    for (const b of bulbCoords) {
      g.fillStyle(0xfde68a, bulbAlpha);
      g.fillCircle(b.x, b.y, 6.0);
      g.fillStyle(0xffffff, bulbAlpha * 1.1);
      g.fillCircle(b.x, b.y, 2.0);
    }
  }

  private updateWindowLighting(overrideHour?: number): void {
    const hour =
      typeof overrideHour === 'number' && !Number.isNaN(overrideHour)
        ? overrideHour
        : new Date().getHours();
    let windowKey = 'window_day';
    this.atmospherePhase = 'day';
    if (hour >= 17 && hour < 20) {
      windowKey = 'window_sunset';
      this.atmospherePhase = 'sunset';
    } else if (hour >= 20 || hour < 6) {
      windowKey = 'window_night';
      this.atmospherePhase = 'night';
    }

    if (this.windowSprite && this.textures.exists(windowKey)) {
      this.windowSprite.setTexture(windowKey);
      this.windowSprite.setAlpha(0);
    }

    this.renderPedestalByPhase();
    this.renderWallDecorations();
    this.renderWindowLightShaft();
  }

  /**
   * Renders the dynamic 3-phase diagonal window light shaft across the wooden plank floor
   * and ambient room illumination (Day golden sunbeam, Sunset rose-amber beam, Night cyan moonbeam + warm lamp pools).
   */
  private renderWindowLightShaft(): void {
    if (!this.windowLightGraphics) return;
    const g = this.windowLightGraphics;
    g.clear();

    if (this.atmospherePhase === 'day') {
      // Warm Golden Morning/Afternoon Sunbeam streaming from the Left Wall Window sill onto the wood floor
      g.fillStyle(0xfef08a, 0.16);
      g.beginPath();
      g.moveTo(82, 235);
      g.lineTo(136, 211);
      g.lineTo(258, 294);
      g.lineTo(156, 336);
      g.closePath();
      g.fillPath();

      // Inner bright sunbeam core
      g.fillStyle(0xfde047, 0.11);
      g.beginPath();
      g.moveTo(90, 231);
      g.lineTo(130, 214);
      g.lineTo(240, 298);
      g.lineTo(172, 326);
      g.closePath();
      g.fillPath();

      // Isometric illuminated sun patch on the wooden planks
      g.fillStyle(0xfef9c3, 0.18);
      g.beginPath();
      g.moveTo(148, 272);
      g.lineTo(206, 244);
      g.lineTo(258, 294);
      g.lineTo(192, 328);
      g.closePath();
      g.fillPath();
    } else if (this.atmospherePhase === 'sunset') {
      // Golden-Hour Sunset Rose-Amber Beam streaming across the room
      g.fillStyle(0xfb923c, 0.19);
      g.beginPath();
      g.moveTo(82, 235);
      g.lineTo(136, 211);
      g.lineTo(274, 304);
      g.lineTo(162, 344);
      g.closePath();
      g.fillPath();

      g.fillStyle(0xf43f5e, 0.11);
      g.beginPath();
      g.moveTo(90, 231);
      g.lineTo(130, 214);
      g.lineTo(254, 306);
      g.lineTo(180, 334);
      g.closePath();
      g.fillPath();

      // Warm amber sunset reflection on floor planks
      g.fillStyle(0xfdba74, 0.17);
      g.beginPath();
      g.moveTo(150, 274);
      g.lineTo(214, 244);
      g.lineTo(272, 300);
      g.lineTo(198, 336);
      g.closePath();
      g.fillPath();
    } else {
      // Starry Night Cool Moonbeam + Cozy Warm Indoor Lamp Pools
      g.fillStyle(0x38bdf8, 0.11);
      g.beginPath();
      g.moveTo(82, 235);
      g.lineTo(136, 211);
      g.lineTo(242, 288);
      g.lineTo(152, 326);
      g.closePath();
      g.fillPath();

      g.fillStyle(0x818cf8, 0.08);
      g.beginPath();
      g.moveTo(146, 268);
      g.lineTo(198, 242);
      g.lineTo(242, 286);
      g.lineTo(184, 318);
      g.closePath();
      g.fillPath();

      // Cozy warm bedside & desk lamp floor pools at night
      g.fillStyle(0xf59e0b, 0.14);
      g.fillCircle(192, 246, 46);
      g.fillStyle(0x38bdf8, 0.10);
      g.fillCircle(262, 282, 38);
      g.fillStyle(0xfbbf24, 0.11);
      g.fillCircle(195, 322, 44);
    }
  }

  /**
   * Initializes deterministic seeded algorithmic particles (sunbeam dust motes, sunset embers, night fireflies).
   * Rendered every frame via `HomeScene.update(time, delta)`.
   */
  private createAlgorithmicAtmosphere(): void {
    if (this.atmosphereGraphics) {
      this.atmosphereGraphics.destroy();
    }
    this.atmosphereGraphics = this.add.graphics().setDepth(-9200);

    let seed = 19890704;
    const nextRand = (): number => {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      return (seed & 0xfffffff) / 0x10000000;
    };

    this.ambientMotes = [];
    for (let i = 0; i < 18; i++) {
      const inSunbeam = i < 8;
      const baseX = inSunbeam
        ? 102 + nextRand() * 135
        : 65 + nextRand() * 260;
      const baseY = inSunbeam
        ? 155 + nextRand() * 155
        : 145 + nextRand() * 215;
      this.ambientMotes.push({
        baseX,
        baseY,
        ampX: 4 + nextRand() * 9,
        ampY: 3 + nextRand() * 7,
        freq: 0.45 + nextRand() * 0.85,
        phase: nextRand() * Math.PI * 2,
        radius: 0.9 + nextRand() * 1.1,
        inSunbeam,
      });
    }

    this.renderAtmosphericParticles(0);
  }

  private renderAtmosphericParticles(timeSec: number): void {
    if (!this.atmosphereGraphics) return;
    const g = this.atmosphereGraphics;
    g.clear();

    for (let i = 0; i < this.ambientMotes.length; i++) {
      const m = this.ambientMotes[i];
      const angle = timeSec * m.freq + m.phase;
      const x = m.baseX + Math.sin(angle) * m.ampX;
      const y = m.baseY + Math.cos(angle * 0.85) * m.ampY;
      const pulse = 0.5 + 0.5 * Math.sin(timeSec * (m.freq * 1.7) + m.phase);

      if (this.atmospherePhase === 'day') {
        const alpha = (m.inSunbeam ? 0.22 : 0.12) + pulse * 0.22;
        const moteColor = m.inSunbeam ? 0xfef08a : 0xffffff;
        g.fillStyle(moteColor, alpha);
        g.fillCircle(x, y, m.radius);
      } else if (this.atmospherePhase === 'sunset') {
        const alpha = (m.inSunbeam ? 0.24 : 0.14) + pulse * 0.24;
        const moteColor = i % 2 === 0 ? 0xfb923c : 0xfde047;
        g.fillStyle(moteColor, alpha);
        g.fillCircle(x, y, m.radius * 1.1);
      } else {
        // Night: glowing fireflies & starlight glints with soft outer halo
        const alpha = 0.15 + pulse * 0.30;
        const fireflyColor = i % 3 === 0 ? 0x38bdf8 : i % 3 === 1 ? 0xfde68a : 0xc084fc;
        g.fillStyle(fireflyColor, alpha * 0.35);
        g.fillCircle(x, y, m.radius * 2.4);
        g.fillStyle(fireflyColor, alpha);
        g.fillCircle(x, y, m.radius);
      }
    }
  }

  private createFloorGrid(): void {
    const gridSize = 8;
    const baseAlpha = this.isDecorMode ? 0.28 : 0.14;
    for (let row = 0; row < gridSize; row++) {
      for (let col = 0; col < gridSize; col++) {
        const pt = IsometricEngine.gridToScreen(col, row);
        // Subtle warm wood-plank sheen (never 0.001 invisible hack) that harmonizes with cozy_room_shell.png
        const tile = this.add.image(pt.isoX, pt.isoY, 'floor_wood')
          .setOrigin(0.5, 0)
          .setAlpha(baseAlpha)
          .setDepth(IsometricEngine.calculateDepth(col, row, -20000));

        this.floorSprites.push(tile);
      }
    }
  }

  // =========================================================================
  // FURNITURE RENDERING, 2.2x-2.4x MULTI-TILE ZONES & COMPACT ACTION PILLS
  // =========================================================================

  private syncStatBadgeGates(): boolean {
    const stats = GameState.getState()?.stats;
    if (!stats) return false;
    let triggeredNewLowAlert = false;

    // 1. Energy -> Bed badge gate
    if (stats.energy >= 100 || stats.energy - this.lastStatSnapshot.energy >= 5) {
      this.suppressedUntilLowStats.energy = true;
      this.lowAlertShownForCurrentDrop.energy = false;
    } else if (stats.energy > HomeScene.STAT_ALERT_THRESHOLD) {
      this.lowAlertShownForCurrentDrop.energy = false;
    } else if (!this.lowAlertShownForCurrentDrop.energy) {
      this.lowAlertShownForCurrentDrop.energy = true;
      this.suppressedUntilLowStats.energy = false;
      triggeredNewLowAlert = true;
    }

    // 2. Hunger -> Fridge badge gate
    if (stats.hunger >= 100 || stats.hunger - this.lastStatSnapshot.hunger >= 5) {
      this.suppressedUntilLowStats.hunger = true;
      this.lowAlertShownForCurrentDrop.hunger = false;
    } else if (stats.hunger > HomeScene.STAT_ALERT_THRESHOLD) {
      this.lowAlertShownForCurrentDrop.hunger = false;
    } else if (!this.lowAlertShownForCurrentDrop.hunger) {
      this.lowAlertShownForCurrentDrop.hunger = true;
      this.suppressedUntilLowStats.hunger = false;
      triggeredNewLowAlert = true;
    }

    // 3. Happiness -> Sofa badge gate
    if (stats.happiness >= 100 || stats.happiness - this.lastStatSnapshot.happiness >= 5) {
      this.suppressedUntilLowStats.happiness = true;
      this.lowAlertShownForCurrentDrop.happiness = false;
    } else if (stats.happiness > HomeScene.STAT_ALERT_THRESHOLD) {
      this.lowAlertShownForCurrentDrop.happiness = false;
    } else if (!this.lowAlertShownForCurrentDrop.happiness) {
      this.lowAlertShownForCurrentDrop.happiness = true;
      this.suppressedUntilLowStats.happiness = false;
      triggeredNewLowAlert = true;
    }

    this.lastStatSnapshot = {
      energy: stats.energy,
      hunger: stats.hunger,
      happiness: stats.happiness,
    };

    if (triggeredNewLowAlert) {
      this.badgesAutoHidden = false;
      this.badgeAutoHideRemainingMs = HomeScene.BADGE_AUTO_HIDE_MS;
    }

    return triggeredNewLowAlert;
  }

  private shouldShowFurnitureBadge(item: PlacedFurniture): boolean {
    if (this.isDecorMode) {
      return false;
    }

    if (this.badgesAutoHidden) {
      return false;
    }

    const stats = GameState.getState()?.stats;
    if (!stats) return false;

    switch (item.typeId) {
      case 'bed':
        if (stats.energy >= 100) return false;
        if (this.suppressedUntilLowStats.energy && stats.energy > HomeScene.STAT_ALERT_THRESHOLD) {
          return false;
        }
        return true;
      case 'fridge':
        if (stats.hunger >= 100) return false;
        if (this.suppressedUntilLowStats.hunger && stats.hunger > HomeScene.STAT_ALERT_THRESHOLD) {
          return false;
        }
        return true;
      case 'sofa':
        if (stats.happiness >= 100) return false;
        if (this.suppressedUntilLowStats.happiness && stats.happiness > HomeScene.STAT_ALERT_THRESHOLD) {
          return false;
        }
        return true;
      case 'desk':
      case 'plant':
      case 'souvenir':
        return !this.initialIntroBadgesDone;
      default:
        return false;
    }
  }

  private tickBadgeAutoHide(deltaMs: number): void {
    if (this.isDecorMode || this.badgesAutoHidden) {
      return;
    }
    this.badgeAutoHideRemainingMs -= deltaMs;
    if (this.badgeAutoHideRemainingMs <= 0) {
      this.badgeAutoHideRemainingMs = 0;
      this.badgesAutoHidden = true;
      this.initialIntroBadgesDone = true;
      this.suppressedUntilLowStats.energy = true;
      this.suppressedUntilLowStats.hunger = true;
      this.suppressedUntilLowStats.happiness = true;
      this.clearFurnitureBadges(true);
    }
  }

  private fadeOutFurnitureBadge(badge: Phaser.GameObjects.Container): void {
    if (!badge || badge.destroyed) return;
    const urgentTween = badge.getData('urgentTween') as Phaser.Tweens.Tween | undefined;
    if (urgentTween) {
      urgentTween.stop();
    }
    this.fadingBadgeContainers.add(badge);
    this.tweens.add({
      targets: badge,
      alpha: 0,
      y: badge.y - 5,
      scaleX: 0.94,
      scaleY: 0.94,
      duration: HomeScene.BADGE_FADE_OUT_MS,
      ease: 'Quad.easeOut',
      onComplete: () => {
        this.fadingBadgeContainers.delete(badge);
        if (!badge.destroyed) {
          badge.destroy();
        }
      },
    });
  }

  private clearFurnitureBadges(animate: boolean = true): void {
    const badges = Array.from(this.furnitureBadgeContainers.values());
    this.furnitureBadgeContainers.clear();
    for (const badge of badges) {
      if (animate) {
        this.fadeOutFurnitureBadge(badge);
      } else {
        const urgentTween = badge.getData('urgentTween') as Phaser.Tweens.Tween | undefined;
        if (urgentTween) {
          urgentTween.stop();
        }
        badge.destroy();
      }
    }
  }

  /**
   * Computes the exact isometric diamond of a furniture item's `width × height` footprint:
   * its four vertices, geometric center, front (South) apex, and on-screen diamond size
   * (40×20 px for 1x1, 60×30 px for 2x1 / 1x2).
   */
  public getFurnitureFootprintCenter(item: PlacedFurniture): {
    x: number;
    y: number;
    frontX: number;
    frontY: number;
    width: number;
    height: number;
    diamondWidth: number;
    diamondHeight: number;
    top: IsoPoint;
    right: IsoPoint;
    bottom: IsoPoint;
    left: IsoPoint;
  } {
    const width = this.getFurnitureWidth(item.typeId, item.rotation || 0);
    const height = this.getFurnitureHeight(item.typeId, item.rotation || 0);
    const top = IsometricEngine.gridToScreen(item.gridCol, item.gridRow);
    const right = IsometricEngine.gridToScreen(item.gridCol + width, item.gridRow);
    const bottom = IsometricEngine.gridToScreen(item.gridCol + width, item.gridRow + height);
    const left = IsometricEngine.gridToScreen(item.gridCol, item.gridRow + height);
    return {
      x: (left.isoX + right.isoX) * 0.5,
      y: (top.isoY + bottom.isoY) * 0.5,
      frontX: bottom.isoX,
      frontY: bottom.isoY,
      width,
      height,
      diamondWidth: right.isoX - left.isoX,
      diamondHeight: bottom.isoY - top.isoY,
      top,
      right,
      bottom,
      left,
    };
  }

  /**
   * Renders soft isometric contact shadows beneath every placed furniture piece
   * and carved 2.5D Mahogany & Brass Display Pedestals beneath placed Flagship State Souvenirs,
   * grounding all items cohesively onto the hand-drawn wooden floor planks.
   */
  private renderCozyFurnitureZones(placed: PlacedFurniture[]): void {
    if (this.roomZonesGraphics) {
      this.roomZonesGraphics.clear();
    }
    if (this.roomPropsGraphics) {
      this.roomPropsGraphics.clear();
    }

    if (!this.furnitureShadowGraphics) {
      this.furnitureShadowGraphics = this.add.graphics().setDepth(-12000);
    }
    const sg = this.furnitureShadowGraphics;
    sg.clear();

    for (const item of placed) {
      const footprint = this.getFurnitureFootprintCenter(item);
      const offset = this.getFurnitureVisualOffset(item);
      const sx = footprint.x + offset.dx;
      const sy = footprint.y + offset.dy;
      const tier = item.tier || 1;
      const clampedTier = tier >= 3 ? 3 : tier === 2 ? 2 : 1;

      if (item.typeId === 'souvenir') {
        // Soft ambient floor contact shadow + warm golden rim aura beneath the Gouache PNG's hand-drawn round oak pedestal
        sg.fillStyle(0x1e110b, 0.24);
        sg.beginPath();
        sg.moveTo(sx, sy - 6.5);
        sg.lineTo(sx + 13.5, sy);
        sg.lineTo(sx, sy + 6.5);
        sg.lineTo(sx - 13.5, sy);
        sg.closePath();
        sg.fillPath();

        sg.lineStyle(1.0, 0xf59e0b, 0.32);
        sg.beginPath();
        sg.moveTo(sx, sy - 7.5);
        sg.lineTo(sx + 15, sy);
        sg.lineTo(sx, sy + 7.5);
        sg.lineTo(sx - 15, sy);
        sg.closePath();
        sg.strokePath();
        continue;
      }

      const spriteKey = this.resolveFurnitureSpriteKey(item);
      const ovalKey = item.typeId === 'sofa' && clampedTier === 1 ? 'sofa_t1' : spriteKey;
      const ovalSpec = HomeScene.ROUND_BASE_OVAL_SHADOWS[ovalKey];
      if (ovalSpec) {
        const flipX = this.shouldFlipFurnitureSprite(item);
        const cx = sx + (flipX ? -ovalSpec.cxOffset : ovalSpec.cxOffset);
        const cy = sy + ovalSpec.cyOffset;
        const tilt = flipX ? -ovalSpec.tiltRad : ovalSpec.tiltRad;

        if (tier >= 3) {
          sg.fillStyle(0xf59e0b, 0.14);
          this.fillIsoOvalPath(sg, cx, cy, ovalSpec.rx + 1.4, ovalSpec.ry + 0.7, tilt);
        } else if (tier === 2) {
          sg.fillStyle(0x38bdf8, 0.10);
          this.fillIsoOvalPath(sg, cx, cy, ovalSpec.rx + 1.2, ovalSpec.ry + 0.6, tilt);
        }

        sg.fillStyle(0x1e110b, 0.24);
        this.fillIsoOvalPath(sg, cx, cy, ovalSpec.rx, ovalSpec.ry, tilt);
        continue;
      }

      // Shape-matched 2:1 isometric parallelogram contact shadow wrapping each piece's floor-contact soles.
      let c0: number;
      let c1: number;
      let r0: number;
      let r1: number;

      if (item.typeId === 'fridge' || item.typeId === 'plant') {
        const [backMin, frontMax, sideMin, sideMax] =
          item.typeId === 'fridge'
            ? HomeScene.FRIDGE_SHADOW_GRID_BOUNDS[clampedTier]
            : HomeScene.BONSAI_SHADOW_GRID_BOUNDS;
        const rotated = (item.rotation || 0) === 90;
        c0 = item.gridCol + (rotated ? sideMin : backMin);
        c1 = item.gridCol + (rotated ? sideMax : frontMax);
        r0 = item.gridRow + (rotated ? backMin : sideMin);
        r1 = item.gridRow + (rotated ? frontMax : sideMax);
      } else {
        const axis = this.getFurnitureFootprintAxis(item);
        const [longMin, longMax, shortMin, shortMax] =
          item.typeId === 'desk'
            ? HomeScene.DESK_SHADOW_GRID_BOUNDS[clampedTier]
            : item.typeId === 'sofa'
              ? HomeScene.SOFA_RECT_SHADOW_GRID_BOUNDS[clampedTier >= 3 ? 3 : 2]
              : HomeScene.BED_SHADOW_GRID_BOUNDS[clampedTier];
        c0 = item.gridCol + (axis === 'col' ? longMin : shortMin);
        c1 = item.gridCol + (axis === 'col' ? longMax : shortMax);
        r0 = item.gridRow + (axis === 'col' ? shortMin : longMin);
        r1 = item.gridRow + (axis === 'col' ? shortMax : longMax);
      }

      const sTop = IsometricEngine.gridToScreen(c0, r0);
      const sRight = IsometricEngine.gridToScreen(c1, r0);
      const sBot = IsometricEngine.gridToScreen(c1, r1);
      const sLeft = IsometricEngine.gridToScreen(c0, r1);
      const topVertexX = sTop.isoX;
      const topVertexY = sTop.isoY;
      const rightVertexX = sRight.isoX;
      const rightVertexY = sRight.isoY;
      const botVertexX = sBot.isoX;
      const botVertexY = sBot.isoY;
      const leftVertexX = sLeft.isoX;
      const leftVertexY = sLeft.isoY;

      // Tier 2 & Tier 3 subtle floor light reflection under upgraded furniture
      if (tier >= 3) {
        sg.fillStyle(0xf59e0b, 0.14);
        sg.beginPath();
        sg.moveTo(topVertexX, topVertexY - 1);
        sg.lineTo(rightVertexX + 2, rightVertexY);
        sg.lineTo(botVertexX, botVertexY + 1);
        sg.lineTo(leftVertexX - 2, leftVertexY);
        sg.closePath();
        sg.fillPath();
      } else if (tier === 2) {
        sg.fillStyle(0x38bdf8, 0.10);
        sg.beginPath();
        sg.moveTo(topVertexX, topVertexY - 1);
        sg.lineTo(rightVertexX + 1, rightVertexY);
        sg.lineTo(botVertexX, botVertexY + 1);
        sg.lineTo(leftVertexX - 1, leftVertexY);
        sg.closePath();
        sg.fillPath();
      }

      sg.fillStyle(0x1e110b, 0.24);
      sg.beginPath();
      sg.moveTo(topVertexX, topVertexY);
      sg.lineTo(rightVertexX, rightVertexY);
      sg.lineTo(botVertexX, botVertexY);
      sg.lineTo(leftVertexX, leftVertexY);
      sg.closePath();
      sg.fillPath();
    }
  }

  /**
   * Draws a filled 2:1 isometric oval path on a MicroCanvasEngine Graphics context
   * using polygon path primitives (beginPath/moveTo/lineTo/closePath/fillPath).
   */
  private fillIsoOvalPath(
    sg: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    tiltRad: number = 0,
    segments: number = 24
  ): void {
    const cosT = Math.cos(tiltRad);
    const sinT = Math.sin(tiltRad);
    sg.beginPath();
    for (let i = 0; i < segments; i++) {
      const theta = (i / segments) * Math.PI * 2;
      const ex = rx * Math.cos(theta);
      const ey = ry * Math.sin(theta);
      const px = cx + ex * cosT - ey * sinT;
      const py = cy + ex * sinT + ey * cosT;
      if (i === 0) {
        sg.moveTo(px, py);
      } else {
        sg.lineTo(px, py);
      }
    }
    sg.closePath();
    sg.fillPath();
  }

  /**
   * Renders animated Tier 2 / Tier 3 luxury glints and 5-State Souvenir starlight sparkles every frame.
   * Every effect is positioned relative to the sprite's real on-screen bounds so it tracks the
   * furniture's scale, floor anchor, and flip orientation.
   */
  private renderDynamicFurnitureEffects(timeSec: number): void {
    if (!this.furnitureEffectsGraphics) {
      this.furnitureEffectsGraphics = this.add.graphics().setDepth(45000);
    }
    const fg = this.furnitureEffectsGraphics;
    fg.clear();

    const placed = GameState.getState()?.placedFurniture || [];
    for (let i = 0; i < placed.length; i++) {
      const item = placed[i];
      const sprite = this.furnitureSprites.get(item.id);
      if (!sprite) continue;
      const bounds = this.getSpriteVisualBounds(sprite);
      const sx = (bounds.left + bounds.right) * 0.5;
      const bodyW = bounds.right - bounds.left;
      const bodyH = bounds.bottom - bounds.top;
      const tier = item.tier || 1;
      const pulse = 0.5 + 0.5 * Math.sin(timeSec * 2.8 + i * 1.3);

      if (item.typeId === 'souvenir') {
        // Sparkling museum spotlight & floating golden starlight motes around State Souvenirs (NY, CA, TX, FL, WA)
        const glowY = bounds.top + bodyH * 0.45;
        fg.fillStyle(0xfde68a, 0.12 + pulse * 0.10);
        fg.fillCircle(sx, glowY, 12);

        const sparkX = sx + Math.cos(timeSec * 2.1 + i) * 8;
        const sparkY = glowY - 3 + Math.sin(timeSec * 2.7 + i) * 4;
        fg.fillStyle(0xf59e0b, 0.55 + pulse * 0.4);
        fg.fillCircle(sparkX, sparkY, 1.6);
        fg.fillStyle(0xffffff, 0.75);
        fg.fillCircle(sparkX, sparkY, 0.8);
      } else if (tier >= 3) {
        // Tier 3 Royal / Executive Artisan shimmer & golden crest badge
        const topY = bounds.top + 4;
        fg.fillStyle(0xf59e0b, 0.14 + pulse * 0.12);
        fg.fillCircle(sx, topY + 8, Math.min(18, bodyW * 0.4));

        const glintX = sx + Math.sin(timeSec * 2.2 + i) * Math.min(11, bodyW * 0.25);
        const glintY = topY + Math.cos(timeSec * 1.9 + i) * 4;
        fg.fillStyle(0xfde68a, 0.65 + pulse * 0.3);
        fg.fillCircle(glintX, glintY, 1.8);
      } else if (item.typeId === 'desk') {
        // PC / Studio Desk subtle cyan monitor ambient glow
        fg.fillStyle(0x38bdf8, (tier >= 2 ? 0.10 : 0.06) + pulse * 0.06);
        fg.fillCircle(sx, bounds.top + bodyH * 0.3, 11);
      } else if (item.typeId === 'bed') {
        // Warm golden bedside lamp halo on the headboard side (mirrors with the sprite)
        const lampX = sx + (sprite.flipX ? 1 : -1) * bodyW * 0.2;
        fg.fillStyle(0xfbbf24, (this.atmospherePhase === 'night' ? 0.14 : 0.06) + pulse * 0.05);
        fg.fillCircle(lampX, bounds.top + bodyH * 0.3, 10);
      }
    }
  }

  /**
   * On-screen axis-aligned bounds of a sprite after origin, scale, flip, and texture frame are applied.
   * MicroCanvasEngine mirrors a flipped sprite around its origin, so `originX` swaps sides.
   */
  private getSpriteVisualBounds(sprite: Phaser.GameObjects.Image): {
    left: number;
    right: number;
    top: number;
    bottom: number;
  } {
    const displayW = sprite.width * Math.abs(sprite.scaleX);
    const displayH = sprite.height * Math.abs(sprite.scaleY);
    const leftFraction = sprite.flipX ? 1 - sprite.originX : sprite.originX;
    const left = sprite.x - leftFraction * displayW;
    const top = sprite.y - sprite.originY * displayH;
    return {
      left,
      right: left + displayW,
      top,
      bottom: top + displayH,
    };
  }

  /**
   * Per-tier scale keeping every PNG illustration's content width under `diamondWidth × 0.92`
   * of its footprint (≤ 55px for 2-cell items, ≤ 36px for 1x1 items).
   */
  private getFurnitureVisualScale(typeId: string, tier: number = 1): number {
    switch (typeId) {
      case 'desk':
        return tier === 1 ? 1.18 : tier === 2 ? 1.12 : 1.08;
      case 'bed':
        return tier === 1 ? 0.75 : tier === 2 ? 0.745 : 0.74;
      case 'sofa':
        return tier === 1 ? 1.18 : tier === 2 ? 1.12 : 0.9;
      case 'fridge':
        return tier === 1 ? 1.15 : tier === 2 ? 1.08 : 1.0;
      case 'plant':
        return tier === 1 ? 1.12 : tier === 2 ? 1.05 : 1.08;
      case 'souvenir':
        return 1.15;
      default:
        return 1.15;
    }
  }

  /**
   * Sprite origin placing the illustration's floor anchor (`FURNITURE_FLOOR_ANCHOR`) on the sprite
   * position. The PNG is letterboxed bottom-center into the logical canvas
   * (`PixelArtGenerator.drawPngFittedToContext`), so the content-relative anchor is mapped through
   * the same contain-fit to a canvas-relative origin.
   */
  private getSpriteFloorOrigin(spriteKey: string): { x: number; y: number } {
    const [anchorX, anchorY] = HomeScene.FURNITURE_FLOOR_ANCHOR[spriteKey] ?? HomeScene.DEFAULT_FLOOR_ANCHOR;
    const frame = this.textures.get(spriteKey)?.getFrame();
    const bbox = PixelArtGenerator.PNG_FURNITURE_CONTENT_BBOX[spriteKey];
    if (!frame || !bbox || frame.width <= 0 || frame.height <= 0) {
      return { x: anchorX, y: anchorY };
    }
    const fit = Math.min(frame.width / bbox[2], frame.height / bbox[3]);
    const contentWidth = bbox[2] * fit;
    const contentHeight = bbox[3] * fit;
    const letterboxLeft = (frame.width - contentWidth) * 0.5;
    const letterboxTop = frame.height - contentHeight;
    return {
      x: (letterboxLeft + anchorX * contentWidth) / frame.width,
      y: (letterboxTop + anchorY * contentHeight) / frame.height,
    };
  }

  /**
   * Fine vertical nudge of the sprite anchor below the footprint center (`getFurnitureFootprintCenter`).
   * Low beds sit almost exactly on the center; upright pieces rest 3px forward so their feet read as
   * standing on the floor rather than floating at the diamond's midline.
   */
  private getFurnitureVisualOffset(item: PlacedFurniture): { dx: number; dy: number } {
    return item.typeId === 'bed' ? { dx: 0, dy: 1 } : { dx: 0, dy: 3 };
  }

  /**
   * Whether the PNG must be mirrored so its long side runs along the occupied footprint.
   * 2-cell pieces compare the footprint axis with the axis the illustration was painted along;
   * 1x1 pieces simply mirror when rotated 90°.
   */
  private shouldFlipFurnitureSprite(item: PlacedFurniture): boolean {
    const nativeAxis = HomeScene.FURNITURE_SPRITE_NATIVE_AXIS[this.resolveFurnitureSpriteKey(item)];
    if (!nativeAxis) {
      return item.rotation === 90;
    }
    return this.getFurnitureFootprintAxis(item) !== nativeAxis;
  }

  /** `row` when the footprint is longer along the row axis (1x2), otherwise `col`. */
  private getFurnitureFootprintAxis(item: PlacedFurniture): 'col' | 'row' {
    const rotation = item.rotation || 0;
    return this.getFurnitureHeight(item.typeId, rotation) > this.getFurnitureWidth(item.typeId, rotation)
      ? 'row'
      : 'col';
  }

  public renderFurniture(): void {
    this.syncStatBadgeGates();
    this.renderWallDecorations();

    // Clear existing sprites
    for (const sprite of this.furnitureSprites.values()) {
      sprite.destroy();
    }
    this.furnitureSprites.clear();

    for (const chairSprite of this.deskChairSprites.values()) {
      chairSprite.destroy();
    }
    this.deskChairSprites.clear();

    const placed = GameState.getState().placedFurniture || [];
    this.renderCozyFurnitureZones(placed);

    const nextVisibleBadgeIds = new Set<string>();
    for (const item of placed) {
      if (this.shouldShowFurnitureBadge(item)) {
        nextVisibleBadgeIds.add(item.id);
      }
    }

    for (const [id, badge] of this.furnitureBadgeContainers.entries()) {
      if (nextVisibleBadgeIds.has(id)) {
        const urgentTween = badge.getData('urgentTween') as Phaser.Tweens.Tween | undefined;
        if (urgentTween) {
          urgentTween.stop();
        }
        badge.destroy();
      } else {
        this.fadeOutFurnitureBadge(badge);
      }
    }
    this.furnitureBadgeContainers.clear();

    for (const item of placed) {
      this.spawnFurnitureSprite(item);
    }

    if (this.isDecorMode) {
      this.renderGridOverlay();
    }
    this.renderDynamicFurnitureEffects(this.atmosphereTimeSec);
    this.renderDecorActionMenu();
    this.renderHomeCozyShelfBar();
  }

  private spawnFurnitureSprite(item: PlacedFurniture): void {
    const spriteKey = this.resolveFurnitureSpriteKey(item);
    const footprint = this.getFurnitureFootprintCenter(item);

    // Compute front corner depth so larger items sort cleanly
    const { width, height } = footprint;
    const frontCol = item.gridCol + width - 1;
    const frontRow = item.gridRow + height - 1;
    const depth = IsometricEngine.calculateDepth(frontCol, frontRow, 10);

    const visualScale = this.getFurnitureVisualScale(item.typeId, item.tier || 1);
    const offset = this.getFurnitureVisualOffset(item);
    const flipX = this.shouldFlipFurnitureSprite(item);
    const spriteX = footprint.x + offset.dx;
    const spriteY = footprint.y + offset.dy;
    const origin = this.getSpriteFloorOrigin(spriteKey);

    // Pin the illustration's floor anchor onto the footprint anchor so its base stays inside the diamond
    const sprite = this.add.sprite(spriteX, spriteY, spriteKey)
      .setOrigin(origin.x, origin.y)
      .setScale(visualScale)
      .setDepth(depth)
      .setFlipX(flipX);

    if (this.isDecorMode) {
      if (this.selectedFurnitureForMoveId === item.id) {
        sprite.setTint(0x38bdf8);
      } else if (this.focusedDecorFurnitureId === item.id) {
        sprite.setTint(0xc084fc);
      }
    }

    this.furnitureSprites.set(item.id, sprite);

    // Spawn matching hand-drawn wooden desk chair tucked under the desk's long front edge
    // (South-West edge for a 2x1 desk, South-East edge for a 1x2 desk), legs kept inside the footprint
    if (item.typeId === 'desk' && this.textures.exists('desk_chair_cozy')) {
      const facesSouthEast = this.getFurnitureFootprintAxis(item) === 'row';
      const chairX = spriteX + (facesSouthEast ? HomeScene.DESK_CHAIR_OFFSET_X : -HomeScene.DESK_CHAIR_OFFSET_X);
      const chairY = spriteY + HomeScene.DESK_CHAIR_OFFSET_Y;
      const chairOrigin = this.getSpriteFloorOrigin('desk_chair_cozy');
      const chairSprite = this.add.sprite(chairX, chairY, 'desk_chair_cozy')
        .setOrigin(chairOrigin.x, chairOrigin.y)
        .setScale(this.getDeskChairScale(item))
        .setDepth(depth + 1)
        .setFlipX(facesSouthEast);

      if (this.isDecorMode) {
        if (this.selectedFurnitureForMoveId === item.id) {
          chairSprite.setTint(0x38bdf8);
        } else if (this.focusedDecorFurnitureId === item.id) {
          chairSprite.setTint(0xc084fc);
        }
      }
      this.deskChairSprites.set(item.id, chairSprite);
    }

    if (!this.shouldShowFurnitureBadge(item)) {
      return;
    }

    // Float the action pill just above the scaled furniture's real visual top so it never obscures the item
    const badgeY = this.getSpriteVisualBounds(sprite).top - 6;
    this.spawnFurnitureActionBadge(item, spriteX, badgeY, depth + 400);
  }

  private getDeskChairScale(item: PlacedFurniture): number {
    return this.getFurnitureVisualScale('desk', item.tier || 1) * HomeScene.DESK_CHAIR_SCALE_RATIO;
  }

  /**
   * Renders a compact (~30% smaller), frosted-glass floating action pill anchored high above the furniture.
   * Only urgent alerts (stats <= 20%, e.g., "🍔 Đói! Ăn ngay") are bright and bouncing; normal pills stay subtle.
   */
  private spawnFurnitureActionBadge(
    item: PlacedFurniture,
    x: number,
    y: number,
    depth: number
  ): void {
    const stats = GameState.getState().stats;
    let labelText = '';
    let borderColor = 0x38bdf8;
    let bgColor = 0x0f172a;
    let textColor = '#cbd5e1';
    let isUrgent = false;

    switch (item.typeId) {
      case 'bed':
        if (stats.energy <= HomeScene.STAT_ALERT_THRESHOLD && stats.hunger <= HomeScene.STAT_ALERT_THRESHOLD) {
          labelText = '🛏️ Đói & Mệt!';
          borderColor = 0xf59e0b;
          bgColor = 0x451a03;
          textColor = '#fde68a';
          isUrgent = true;
        } else if (stats.energy <= HomeScene.STAT_ALERT_THRESHOLD) {
          labelText = '🛏️ Mệt! Ngủ (+⚡)';
          borderColor = 0xef4444;
          bgColor = 0x450a0a;
          textColor = '#fecaca';
          isUrgent = true;
        } else {
          labelText = '🛏️ Giường (Ngủ)';
          borderColor = 0x38bdf8;
        }
        break;
      case 'fridge':
        if (stats.hunger <= HomeScene.STAT_ALERT_THRESHOLD) {
          labelText = '🍔 Đói! Ăn ngay';
          borderColor = 0xf97316;
          bgColor = 0x431407;
          textColor = '#fed7aa';
          isUrgent = true;
        } else {
          labelText = '🧊 Tủ lạnh (Ăn)';
          borderColor = 0x10b981;
        }
        break;
      case 'desk':
        labelText = '💻 Bàn PC (Làm)';
        borderColor = 0x6366f1;
        break;
      case 'sofa':
        if (stats.happiness <= HomeScene.STAT_ALERT_THRESHOLD) {
          labelText = '🛋️ Buồn! Thư giãn';
          borderColor = 0xa855f7;
          bgColor = 0x3b0764;
          textColor = '#e9d5ff';
          isUrgent = true;
        } else {
          labelText = '🛋️ Sofa (Chill)';
          borderColor = 0xa855f7;
        }
        break;
      case 'plant':
        labelText = '🪴 Cây (+😄)';
        borderColor = 0x22c55e;
        break;
      case 'souvenir':
        labelText = '🗽 Kỷ vật (+😄)';
        borderColor = 0xfbbf24;
        break;
      default:
        return;
    }

    // 30% smaller font & padding than previous pills
    const badgeWidth = Math.max(48, labelText.length * 3.75 + 9);
    const badgeHeight = 13.5;
    const halfW = badgeWidth / 2;
    const halfH = badgeHeight / 2;
    const radius = 6.75;

    const pillGraphics = this.add.graphics();

    if (isUrgent) {
      // Urgent alert pill: warm outer glow aura + high-contrast capsule
      pillGraphics.fillStyle(borderColor, 0.24);
      pillGraphics.fillRoundedRect(-halfW - 2, -halfH - 1.5, badgeWidth + 4, badgeHeight + 3, radius + 1.5);

      pillGraphics.fillStyle(0x13101d, 0.92);
      pillGraphics.fillRoundedRect(-halfW, -halfH + 1.8, badgeWidth, badgeHeight, radius);
      pillGraphics.fillTriangle(-3, halfH + 1, 3, halfH + 1, 0, halfH + 4.5);

      pillGraphics.fillStyle(bgColor, 0.95);
      pillGraphics.fillRoundedRect(-halfW, -halfH, badgeWidth, badgeHeight, radius);
      pillGraphics.fillTriangle(-2.5, halfH - 0.5, 2.5, halfH - 0.5, 0, halfH + 3);

      pillGraphics.fillStyle(0xffffff, 0.16);
      pillGraphics.fillRoundedRect(-halfW + 3, -halfH + 1.4, badgeWidth - 6, 3.2, 2);

      pillGraphics.lineStyle(1.3, borderColor, 0.98);
      pillGraphics.strokeRoundedRect(-halfW, -halfH, badgeWidth, badgeHeight, radius);
    } else {
      // Quiet, unobtrusive frosted dark-glass pill
      pillGraphics.fillStyle(0x0f172a, 0.64);
      pillGraphics.fillRoundedRect(-halfW, -halfH, badgeWidth, badgeHeight, radius);
      pillGraphics.fillTriangle(-2.2, halfH - 0.4, 2.2, halfH - 0.4, 0, halfH + 2.4);

      pillGraphics.fillStyle(0xffffff, 0.08);
      pillGraphics.fillRoundedRect(-halfW + 2.5, -halfH + 1.2, badgeWidth - 5, 2.6, 1.5);

      pillGraphics.lineStyle(0.9, borderColor, 0.48);
      pillGraphics.strokeRoundedRect(-halfW, -halfH, badgeWidth, badgeHeight, radius);
    }

    const pillText = this.add.text(0, 0, labelText, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: isUrgent ? '7.2px' : '6.6px',
      fontStyle: isUrgent ? 'bold' : '600',
      color: textColor,
    }).setOrigin(0.5, 0.5);

    const container = this.add.container(x, y, [pillGraphics, pillText]);
    container.setDepth(depth);
    container.setData('badgeWidth', badgeWidth);

    if (isUrgent) {
      const urgentTween = this.tweens.add({
        targets: container,
        y: y - 3.5,
        duration: 520,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      container.setData('urgentTween', urgentTween);
    }

    this.furnitureBadgeContainers.set(item.id, container);
  }

  private resolveFurnitureSpriteKey(item: PlacedFurniture): string {
    const tier = item.tier || 1;
    switch (item.typeId) {
      case 'bed':
        return `bed_t${tier}`;
      case 'fridge':
        return `fridge_t${tier}`;
      case 'desk':
        return `desk_t${tier}`;
      case 'sofa':
        return `sofa_t${tier}`;
      case 'plant':
        return tier === 1 ? 'plant_cactus' : tier === 2 ? 'plant_monstera' : 'plant_bonsai';
      case 'souvenir': {
        const rawId = item.customData?.souvenirId || 'souvenir_ny_statue_liberty';
        const customId = normalizeSouvenirId(rawId);
        return this.textures.exists(customId) ? customId : 'souvenir_ny_statue_liberty';
      }
      default:
        return `bed_t1`;
    }
  }

  private getFurnitureWidth(typeId: string, rotation: number = 0): number {
    const dims = DecorSystem.getEffectiveDimensions(typeId, rotation);
    return dims.width;
  }

  private getFurnitureHeight(typeId: string, rotation: number = 0): number {
    const dims = DecorSystem.getEffectiveDimensions(typeId, rotation);
    return dims.height;
  }

  // =========================================================================
  // PLAYER CHARACTER CREATION (4-DIRECTIONAL CHIBI + COZY RIG & ANIMATIONS)
  // =========================================================================

  private syncCameraFocusTarget(): void {
    const px = this.playerSprite ? this.playerSprite.x : 195;
    const py = this.playerSprite ? this.playerSprite.y : 300;
    this.cameraFocusTarget.x = 195 + (px - 195) * 0.22;
    this.cameraFocusTarget.y = 252 + (py - 300) * 0.24;
  }

  private createPlayer(): void {
    const pt = IsometricEngine.gridToScreen(this.playerPos.col, this.playerPos.row);
    const depth = IsometricEngine.calculateDepth(this.playerPos.col, this.playerPos.row, 20);

    // Foot-centered Drop Shadow beneath both isometric shoes (squashes & stretches with step bounce)
    this.shadowSprite = this.add.image(
      pt.isoX,
      pt.isoY + 10 + HomeScene.PLAYER_SHADOW_GROUND_OFFSET_Y,
      'shadow_oval'
    )
      .setOrigin(0.5, 0.5)
      .setScale(
        HomeScene.PLAYER_SHADOW_BASE_SCALE_X,
        HomeScene.PLAYER_SHADOW_BASE_SCALE_X * HomeScene.PLAYER_SHADOW_ASPECT_Y
      )
      .setDepth(depth - 1);

    // 24-frame 4-directional Chibi animation controller sprite (kept at alpha 0 so playerCozySprite renders cleanly without ghosting)
    this.playerSprite = this.add.sprite(pt.isoX, pt.isoY + 10, 'player_chibi')
      .setOrigin(0.5, 0.85)
      .setScale(2.2)
      .setAlpha(0)
      .setDepth(depth);

    this.playerSprite.play('idle_se');

    // Hand-drawn Cozy Character Sprite (#3D251E warm espresso ink outlines + pastel gouache, front & back textures)
    const isBack = this.currentDirection === 'ne' || this.currentDirection === 'nw';
    const cozyKey =
      isBack && this.textures.exists('player_cozy_back')
        ? 'player_cozy_back'
        : this.textures.exists('player_cozy')
        ? 'player_cozy'
        : 'player_chibi';
    this.playerCozySprite = this.add.sprite(
      pt.isoX,
      pt.isoY + 10 + HomeScene.PLAYER_COZY_FOOT_GROUND_OFFSET_Y,
      cozyKey
    )
      .setOrigin(0.5, HomeScene.PLAYER_COZY_FOOT_ORIGIN_Y)
      .setScale(HomeScene.PLAYER_COZY_BASE_SCALE)
      .setDepth(depth + 0.1)
      .setFlipX(this.currentDirection === 'sw' || this.currentDirection === 'nw');

    // Dynamic ground dust & activity FX graphics layers
    this.playerRigGraphics = this.add.graphics().setDepth(depth + 0.15);
    this.playerFxGraphics = this.add.graphics().setDepth(depth + 0.25);

    this.updateCharacterAnimationFrame(0, 16);
    this.syncCameraFocusTarget();
  }

  private spawnFootstepDustPuff(
    footX: number,
    footY: number,
    dirSign: number,
    intensity: number = 1
  ): void {
    const count = 2;
    for (let i = 0; i < count; i++) {
      if (this.footstepDustPuffs.length >= 12) {
        this.footstepDustPuffs.shift();
      }
      const spread = i === 0 ? -1.4 : 1.6;
      this.footstepDustPuffs.push({
        x: footX - dirSign * (4.8 + i * 1.8),
        y: footY - 0.5 + spread * 0.45,
        vx: -dirSign * (7 + i * 4.5) * intensity,
        vy: -(4.5 + i * 2.5) * intensity,
        ageMs: 0,
        ttlMs: 240 + i * 45,
        radius: (2.2 - i * 0.45) * intensity,
      });
    }
  }

  private resolveWalkCycleTextureKey(isBackFacing: boolean): string {
    const normalizedPhase =
      ((this.walkCyclePhase % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    const phaseIndex = Math.floor(normalizedPhase / (Math.PI / 2)) % 4;

    let candidateKey: string;
    if (phaseIndex === 0) {
      candidateKey = isBackFacing ? 'player_cozy_back_walk1' : 'player_cozy_walk1';
    } else if (phaseIndex === 2) {
      candidateKey = isBackFacing ? 'player_cozy_back_walk2' : 'player_cozy_walk2';
    } else {
      candidateKey = isBackFacing ? 'player_cozy_back' : 'player_cozy';
    }

    if (this.textures.exists(candidateKey)) {
      return candidateKey;
    }
    const fallbackKey = isBackFacing ? 'player_cozy_back' : 'player_cozy';
    return this.textures.exists(fallbackKey) ? fallbackKey : 'player_cozy';
  }

  /**
   * Drives 60 FPS foot-anchored paper-cutout motion on `playerCozySprite` and `shadowSprite`:
   * - Walking: double-frequency vertical step bounce, side-to-side sway, volume-preserving squash-and-stretch
   *   synchronized with the 4-step texture cycle (`walk1 -> base -> walk2 -> base`), dynamic shadow pulse & warm dust puffs.
   * - Stopping & Idle: unconditionally stable damped settle spring + smooth 60 FPS breathing anchored strictly at the shoe soles.
   */
  private updateCharacterAnimationFrame(
    timeSec: number,
    deltaMs: number,
    rawDeltaMs: number = deltaMs
  ): void {
    if (!this.playerSprite) return;

    const px = this.playerSprite.x;
    const py = this.playerSprite.y;
    const footBaseY = py + HomeScene.PLAYER_COZY_FOOT_GROUND_OFFSET_Y;
    const baseScale = HomeScene.PLAYER_COZY_BASE_SCALE;
    const baseDepth = this.playerSprite.depth;

    if (this.playerRigGraphics) {
      this.playerRigGraphics.setDepth(baseDepth + 0.15);
      this.playerRigGraphics.clear();
    }
    if (this.playerFxGraphics) {
      this.playerFxGraphics.setDepth(baseDepth + 0.25);
      this.playerFxGraphics.clear();
    }

    const isBackFacing = this.currentDirection === 'ne' || this.currentDirection === 'nw';
    const isLeftFacing = this.currentDirection === 'sw' || this.currentDirection === 'nw';
    const dirSign = isLeftFacing ? -1 : 1;

    if (this.isMoving) {
      this.wasCharacterMoving = true;
      this.characterSettleDisplacement = 0;
      this.characterSettleVelocity = 0;

      // ~9.1 steps/sec (28.5 rad/s, where each step is PI radians) so even a 165ms 1-tile walk cycles through walk1 -> base -> walk2
      this.walkCyclePhase += (deltaMs / 1000) * 28.5;
      const phase = this.walkCyclePhase;
      const activeFrameKey = this.resolveWalkCycleTextureKey(isBackFacing);

      const bounceMag = Math.abs(Math.sin(phase));
      const bounceY = -bounceMag * 3.2;
      const swayRotation = Math.cos(phase) * 0.055;
      const squash = 1 + 0.045 * Math.cos(phase * 2);
      const scaleY = baseScale * squash;
      const scaleX = baseScale / Math.sqrt(squash);

      if (this.playerCozySprite) {
        if (this.textures.exists(activeFrameKey)) {
          this.playerCozySprite.setTexture(activeFrameKey);
        }
        this.playerCozySprite.setOrigin(0.5, HomeScene.PLAYER_COZY_FOOT_ORIGIN_Y);
        this.playerCozySprite.setPosition(px, footBaseY + bounceY);
        this.playerCozySprite.setScale(scaleX, scaleY);
        this.playerCozySprite.setRotation(swayRotation);
        this.playerCozySprite.setFlipX(isLeftFacing);
        this.playerCozySprite.setDepth(baseDepth + 0.1);
      }

      if (this.shadowSprite) {
        this.shadowSprite.setPosition(px, py + HomeScene.PLAYER_SHADOW_GROUND_OFFSET_Y);
        this.shadowSprite.setDepth(baseDepth - 1);
        const shadowContact = 1 - bounceMag;
        const shadowScaleX =
          HomeScene.PLAYER_SHADOW_BASE_SCALE_X * (1 + 0.045 * shadowContact - 0.14 * bounceMag);
        this.shadowSprite.setScale(
          shadowScaleX,
          shadowScaleX * HomeScene.PLAYER_SHADOW_ASPECT_Y
        );
        this.shadowSprite.setAlpha(0.94 - bounceMag * 0.16);
      }

      const currentStepIndex = Math.floor(phase / Math.PI);
      if (currentStepIndex !== this.lastFootStrikeStep) {
        this.lastFootStrikeStep = currentStepIndex;
        this.spawnFootstepDustPuff(px, footBaseY, dirSign, 1);
      }
      if (this.playerRigGraphics && bounceMag < 0.24) {
        this.playerRigGraphics.fillStyle(0xfef3c7, 0.26);
        this.playerRigGraphics.fillCircle(px - dirSign * 5.5, footBaseY - 0.5, 2.1);
      }
    } else {
      if (this.wasCharacterMoving) {
        this.wasCharacterMoving = false;
        this.lastFootStrikeStep = -1;
        this.characterSettleDisplacement = -0.042;
        this.characterSettleVelocity = 0.22;
        this.spawnFootstepDustPuff(px, footBaseY, dirSign, 0.75);
      }

      this.walkCyclePhase = 0;

      // Sub-stepped damped harmonic settle spring (unconditionally stable for any frame delta)
      if (this.characterSettleDisplacement !== 0 || this.characterSettleVelocity !== 0) {
        const elapsedMs = Math.max(rawDeltaMs, deltaMs, 0);
        let remainingSec = Math.min(elapsedMs / 1000, 0.75);
        const stiffness = 195;
        const damping = 17.5;
        while (remainingSec > 0) {
          const stepSec = Math.min(remainingSec, 1 / 120);
          const accel =
            -stiffness * this.characterSettleDisplacement - damping * this.characterSettleVelocity;
          this.characterSettleVelocity += accel * stepSec;
          this.characterSettleDisplacement += this.characterSettleVelocity * stepSec;
          remainingSec -= stepSec;
        }
        if (
          Math.abs(this.characterSettleDisplacement) < 0.0002 &&
          Math.abs(this.characterSettleVelocity) < 0.001
        ) {
          this.characterSettleDisplacement = 0;
          this.characterSettleVelocity = 0;
        }
      }

      const breath = Math.sin(timeSec * 2.6);
      const settleSquash = Phaser.Math.Clamp(1 + this.characterSettleDisplacement, 0.9, 1.12);
      const scaleY = baseScale * (1 + 0.016 * breath) * settleSquash;
      const scaleX = (baseScale * (1 - 0.008 * breath)) / Math.sqrt(settleSquash);

      if (this.playerCozySprite) {
        const idleKey =
          isBackFacing && this.textures.exists('player_cozy_back') ? 'player_cozy_back' : 'player_cozy';
        if (this.textures.exists(idleKey)) {
          this.playerCozySprite.setTexture(idleKey);
        }
        this.playerCozySprite.setOrigin(0.5, HomeScene.PLAYER_COZY_FOOT_ORIGIN_Y);
        this.playerCozySprite.setPosition(px, footBaseY);
        this.playerCozySprite.setScale(scaleX, scaleY);
        this.playerCozySprite.setRotation(0);
        this.playerCozySprite.setFlipX(isLeftFacing);
        this.playerCozySprite.setDepth(baseDepth + 0.1);
      }

      if (this.shadowSprite) {
        this.shadowSprite.setPosition(px, py + HomeScene.PLAYER_SHADOW_GROUND_OFFSET_Y);
        this.shadowSprite.setDepth(baseDepth - 1);
        const shadowIdleScaleX =
          HomeScene.PLAYER_SHADOW_BASE_SCALE_X *
          (1 - 0.014 * breath - this.characterSettleDisplacement * 0.45);
        this.shadowSprite.setScale(
          shadowIdleScaleX,
          shadowIdleScaleX * HomeScene.PLAYER_SHADOW_ASPECT_Y
        );
        this.shadowSprite.setAlpha(0.94);
      }
    }

    // Update & render warm heel dust puffs on wooden floor planks
    if (this.footstepDustPuffs.length > 0) {
      const elapsedMs = Math.max(rawDeltaMs, deltaMs, 0);
      const dtSec = Math.min(Math.max(deltaMs, 0) / 1000, 0.1);
      for (let i = this.footstepDustPuffs.length - 1; i >= 0; i--) {
        const puff = this.footstepDustPuffs[i];
        puff.ageMs += elapsedMs;
        if (puff.ageMs >= puff.ttlMs) {
          this.footstepDustPuffs.splice(i, 1);
          continue;
        }
        puff.x += puff.vx * dtSec;
        puff.y += puff.vy * dtSec;
        if (this.playerRigGraphics) {
          const t = puff.ageMs / puff.ttlMs;
          const fade = (1 - t) * (1 - t);
          const r = puff.radius * (0.85 + t * 0.55);
          this.playerRigGraphics.fillStyle(0xfde68a, 0.24 * fade);
          this.playerRigGraphics.fillCircle(puff.x, puff.y, r * 1.25);
          this.playerRigGraphics.fillStyle(0xfef3c7, 0.36 * fade);
          this.playerRigGraphics.fillCircle(puff.x, puff.y, r);
        }
      }
    }

    // Contextual Furniture Activity FX (Sleep Zzz, PC Typing Sparks, Fridge Mist, Sofa Music)
    if (this.activeActivityType !== 'none' && this.activeActivityRemainingMs > 0) {
      this.activeActivityRemainingMs -= deltaMs;
      if (this.activeActivityRemainingMs <= 0) {
        this.activeActivityType = 'none';
        this.activeActivityRemainingMs = 0;
      } else if (this.playerFxGraphics) {
        const fxg = this.playerFxGraphics;
        const p = 0.5 + 0.5 * Math.sin(timeSec * 4.2);
        if (this.activeActivityType === 'sleep') {
          // Floating sleep dream bubbles rising above the character
          for (let k = 0; k < 3; k++) {
            const prog = ((timeSec * 0.75 + k * 0.33) % 1);
            const bx = px + Math.sin(prog * Math.PI * 2 + k) * 9;
            const by = py - 46 - prog * 24;
            fxg.fillStyle(0x38bdf8, (1 - prog) * 0.75);
            fxg.fillCircle(bx, by, 2.2 + k * 0.9);
          }
        } else if (this.activeActivityType === 'work') {
          // Typing energy ring & rising cyan/gold productivity sparks
          fxg.lineStyle(1.3, 0x38bdf8, 0.45 + p * 0.35);
          fxg.strokeCircle(px + dirSign * 10, py - 22, 6 + p * 3);
          const sySpark = py - 42 - ((timeSec * 18) % 16);
          fxg.fillStyle(0x10b981, 0.85);
          fxg.fillCircle(px - 6, sySpark, 2.2);
          fxg.fillStyle(0xf59e0b, 0.85);
          fxg.fillCircle(px + 7, sySpark - 4, 2.0);
        } else if (this.activeActivityType === 'eat') {
          // Cool refreshment mist & warm nourishment glow
          fxg.fillStyle(0x34d399, 0.22 + p * 0.18);
          fxg.fillCircle(px, py - 24, 15);
          fxg.fillStyle(0xf59e0b, 0.85);
          fxg.fillCircle(px + Math.sin(timeSec * 3) * 8, py - 46, 2.5);
        } else if (this.activeActivityType === 'chill') {
          // Harmonic relaxation aura & floating notes
          fxg.lineStyle(1.2, 0xc084fc, 0.4 + p * 0.35);
          fxg.strokeCircle(px, py - 24, 14 + p * 4);
          fxg.fillStyle(0xa855f7, 0.85);
          fxg.fillCircle(px + Math.cos(timeSec * 2.5) * 10, py - 48, 2.4);
        }
      }
    }
  }

  public startCharacterActivity(type: 'sleep' | 'work' | 'eat' | 'chill', durationMs: number = 4200): void {
    this.activeActivityType = type;
    this.activeActivityRemainingMs = durationMs;
  }

  private setupCamera(): void {
    this.syncCameraFocusTarget();
    // Generous bounds so zooming (0.6x - 2.6x) and panning never clip the tall 2.5x room
    this.cameras.main.setBounds(-180, -120, 750, 980);
    this.setCameraZoom(HomeScene.DEFAULT_ZOOM);
    this.cameras.main.centerOn(this.cameraFocusTarget.x, this.cameraFocusTarget.y);

    // Smoothly follow the balanced room-center + player focus target so the 1.35x room fills the screen
    this.cameras.main.startFollow(this.cameraFocusTarget, true, 0.08, 0.08);
  }

  public setCameraZoom(targetZoom: number): void {
    const clamped = Phaser.Math.Clamp(targetZoom, HomeScene.MIN_ZOOM, HomeScene.MAX_ZOOM);
    this.cameras.main.setZoom(clamped);
    EventBus.emit('CAMERA_ZOOM_CHANGED', clamped);
  }

  // =========================================================================
  // INPUT, DIRECT TOUCH INTERACTION, PINCH/WHEEL ZOOM & DRAG PAN
  // =========================================================================

  /**
   * Returns true if a DOM modal is open, or if a DOM overlay button (Modal, TopHUD, NavDock)
   * was just interacted with, preventing Phaser window mouseup/touchend from bleeding into the canvas.
   */
  private isBlockedByDomOverlay(pointer?: Phaser.Input.Pointer): boolean {
    if (typeof document !== 'undefined') {
      const modalContainer = document.getElementById('modal-container');
      if (modalContainer && modalContainer.classList.contains('active')) {
        return true;
      }
    }

    if (typeof window !== 'undefined') {
      const lastDomTime = (window as unknown as { __lastDomUiEventTime?: number }).__lastDomUiEventTime || 0;
      if (Date.now() - lastDomTime < 220) {
        return true;
      }
    }

    const rawEvtTarget = (pointer?.event as Event | undefined)?.target as HTMLElement | null;
    if (rawEvtTarget && rawEvtTarget.tagName && rawEvtTarget.tagName !== 'CANVAS') {
      return true;
    }

    return false;
  }

  private setupInputHandlers(): void {
    // Enable 2nd touch pointer for two-finger pinch-to-zoom on mobile
    this.input.addPointer(1);

    // 1. Mouse Wheel Zoom
    this.input.on('wheel', (pointer: Phaser.Input.Pointer, _gameObjects: unknown[], _deltaX: number, deltaY: number) => {
      if (this.isBlockedByDomOverlay(pointer)) return;
      const zoomStep = deltaY > 0 ? -0.12 : 0.12;
      this.setCameraZoom(this.cameras.main.zoom + zoomStep);
    });

    // 2. Pointer Down: Record start coordinates ONLY if pointer started on the Phaser CANVAS while no modal is active
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.isBlockedByDomOverlay(pointer)) {
        this.pointerDownOnCanvas = false;
        this.isDraggingFurniture = false;
        return;
      }
      if (pointer.downElement && pointer.downElement.tagName !== 'CANVAS') {
        this.pointerDownOnCanvas = false;
        this.isDraggingFurniture = false;
        return;
      }

      this.pointerDownOnCanvas = true;

      if (this.input.pointer1.isDown && this.input.pointer2.isDown) {
        this.pinchStartDist = Phaser.Math.Distance.Between(
          this.input.pointer1.x,
          this.input.pointer1.y,
          this.input.pointer2.x,
          this.input.pointer2.y
        );
        this.pinchStartZoom = this.cameras.main.zoom;
        this.isDraggingCamera = true;
        this.isDraggingFurniture = false;
        return;
      }

      this.isDraggingCamera = false;
      this.isDraggingFurniture = false;
      this.furnitureDragMoved = false;
      this.pointerDownScreenX = pointer.x ?? pointer.worldX;
      this.pointerDownScreenY = pointer.y ?? pointer.worldY;
      this.cameraScrollStartX = this.cameras.main.scrollX;
      this.cameraScrollStartY = this.cameras.main.scrollY;

      // In Decor Mode, pressing down on any furniture piece immediately selects it for dragging/moving
      if (this.isDecorMode) {
        const gridCont = IsometricEngine.screenToGrid(pointer.worldX, pointer.worldY);
        const c = Math.floor(gridCont.col);
        const r = Math.floor(gridCont.row);
        const hitItem = this.getFurnitureAtScreenPoint(
          pointer.worldX,
          pointer.worldY,
          IsometricEngine.isWithinBounds(c, r) ? { col: c, row: r } : null
        );
        if (hitItem) {
          this.wasFurnitureSelectedBeforePointerDown = this.selectedFurnitureForMoveId === hitItem.id;
          this.focusedDecorFurnitureId = hitItem.id;
          this.selectedFurnitureForMoveId = hitItem.id;
          this.isDraggingFurniture = true;
          this.dragStartGridCol = c;
          this.dragStartGridRow = r;
          this.renderFurniture();
          this.renderGridOverlay();
        } else {
          this.wasFurnitureSelectedBeforePointerDown = false;
          this.dragStartGridCol = -1;
          this.dragStartGridRow = -1;
        }
      }
    });

    // 3. Pointer Move: Handle 2-finger pinch zoom, Decor furniture drag-to-move, OR 1-finger camera pan
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown || !this.pointerDownOnCanvas || this.isBlockedByDomOverlay(pointer)) {
        return;
      }

      // Two-finger pinch-to-zoom
      if (this.input.pointer1.isDown && this.input.pointer2.isDown) {
        this.isDraggingFurniture = false;
        const currentDist = Phaser.Math.Distance.Between(
          this.input.pointer1.x,
          this.input.pointer1.y,
          this.input.pointer2.x,
          this.input.pointer2.y
        );
        if (this.pinchStartDist > 10) {
          const scale = currentDist / this.pinchStartDist;
          this.setCameraZoom(this.pinchStartZoom * scale);
        }
        this.isDraggingCamera = true;
        return;
      }

      // Dragging selected furniture in Decor Move Mode (requires >= 6px drag threshold and crossing into a new grid cell)
      if (this.isDecorMode && this.isDraggingFurniture && this.selectedFurnitureForMoveId) {
        const curX = pointer.x ?? pointer.worldX;
        const curY = pointer.y ?? pointer.worldY;
        if (Math.hypot(curX - this.pointerDownScreenX, curY - this.pointerDownScreenY) < 6) {
          return;
        }
        const gridCont = IsometricEngine.screenToGrid(pointer.worldX, pointer.worldY);
        const targetCol = Math.floor(gridCont.col);
        const targetRow = Math.floor(gridCont.row);
        if (
          IsometricEngine.isWithinBounds(targetCol, targetRow) &&
          (targetCol !== this.dragStartGridCol || targetRow !== this.dragStartGridRow)
        ) {
          this.dragStartGridCol = targetCol;
          this.dragStartGridRow = targetRow;
          const currentItem = (GameState.getState().placedFurniture || []).find(
            (f) => f.id === this.selectedFurnitureForMoveId
          );
          if (currentItem && (currentItem.gridCol !== targetCol || currentItem.gridRow !== targetRow)) {
            const moved = DecorSystem.moveFurniture(this.selectedFurnitureForMoveId, targetCol, targetRow);
            if (moved) {
              this.furnitureDragMoved = true;
              this.renderFurniture();
              this.renderGridOverlay();
            }
          }
        }
        return;
      }

      // Single-pointer drag-to-pan (if dragged beyond 8px threshold)
      const dx = (pointer.x ?? pointer.worldX) - this.pointerDownScreenX;
      const dy = (pointer.y ?? pointer.worldY) - this.pointerDownScreenY;
      if (Math.hypot(dx, dy) > 8) {
        this.isDraggingCamera = true;
        this.cameras.main.stopFollow();
        const zoom = this.cameras.main.zoom || 1;
        this.cameras.main.scrollX = this.cameraScrollStartX - dx / zoom;
        this.cameras.main.scrollY = this.cameraScrollStartY - dy / zoom;
      }
    });

    // 4. Single Unified Pointer Up Dispatcher:
    // Executes at most ONCE per tap, strictly when the tap started & ended on the canvas.
    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (!this.pointerDownOnCanvas) {
        this.isDraggingFurniture = false;
        this.furnitureDragMoved = false;
        return;
      }
      this.handlePointerUp(pointer);
    });
  }

  /**
   * Public pointer-up handler for canvas taps and programmatic E2E test invocations.
   */
  public handlePointerUp(pointer: Phaser.Input.Pointer): void {
    const wasAlreadySelected = this.wasFurnitureSelectedBeforePointerDown;
    this.pointerDownOnCanvas = false;
    this.wasFurnitureSelectedBeforePointerDown = false;

    if (this.isBlockedByDomOverlay(pointer)) {
      this.isDraggingFurniture = false;
      this.furnitureDragMoved = false;
      return;
    }

    if (this.isDraggingFurniture) {
      const didMove = this.furnitureDragMoved;
      this.isDraggingFurniture = false;
      this.furnitureDragMoved = false;
      this.dragStartGridCol = -1;
      this.dragStartGridRow = -1;
      if (didMove) {
        this.renderFurniture();
        this.renderGridOverlay();
        return;
      }
    }

    if (this.isDraggingCamera) {
      this.isDraggingCamera = false;
      return;
    }

    const now = Date.now();
    if (now - this.lastTapActionTimestamp < 120) {
      return;
    }
    this.lastTapActionTimestamp = now;

    // 4a. Compute exact continuous & discrete isometric grid coordinates (without clamping out-of-bounds taps!)
    const gridContinuous = IsometricEngine.screenToGrid(pointer.worldX, pointer.worldY);
    const col = Math.floor(gridContinuous.col);
    const row = Math.floor(gridContinuous.row);
    const inBounds = IsometricEngine.isWithinBounds(col, row);

    // 4b. Check if tap hit any furniture floating badge or tight visual sprite body (front-to-back depth order)
    const hitFurniture = this.getFurnitureAtScreenPoint(pointer.worldX, pointer.worldY, inBounds ? { col, row } : null);
    if (hitFurniture) {
      // If a 2x1 item was ALREADY selected and user tapped its secondary footprint cell, treat as tile move!
      if (
        this.isDecorMode &&
        wasAlreadySelected &&
        this.selectedFurnitureForMoveId === hitFurniture.id &&
        inBounds &&
        (col !== hitFurniture.gridCol || row !== hitFurniture.gridRow)
      ) {
        this.handleTileTap(col, row);
        return;
      }
      console.log(
        `[EventLog] [Canvas Tap] world=(${Math.round(pointer.worldX)},${Math.round(pointer.worldY)}) -> Furniture "${hitFurniture.typeId}" (${hitFurniture.id}) at (${hitFurniture.gridCol},${hitFurniture.gridRow})`
      );
      this.handleFurnitureTap(hitFurniture);
      return;
    }

    // 4c. If tap is strictly inside the 8x8 floor grid, handle floor tile tap (moves selected item in Decor Mode, or walks in Living Mode)
    if (inBounds) {
      console.log(
        `[EventLog] [Canvas Tap] world=(${Math.round(pointer.worldX)},${Math.round(pointer.worldY)}) -> Floor Tile (${col},${row})`
      );
      this.handleTileTap(col, row);
    } else {
      console.log(
        `[EventLog] [Canvas Tap Ignored] Outside 8x8 room bounds: world=(${Math.round(pointer.worldX)},${Math.round(pointer.worldY)}), grid=(${col},${row})`
      );
    }
  }

  /**
   * Depth-sorted furniture hit-test:
   * 1. Checks floating action badges (front-to-back).
   * 2. Checks if the tapped floor cell (tappedCell) is occupied by a furniture footprint.
   * 3. Checks tight visual sprite body around tile anchor (front-to-back) without bleeding into front rows.
   */
  private getFurnitureAtScreenPoint(
    worldX: number,
    worldY: number,
    tappedCell: GridCoord | null = null
  ): PlacedFurniture | null {
    const placed = [...(GameState.getState().placedFurniture || [])];

    // Sort front-to-back (highest depth first) so closer furniture always takes priority
    placed.sort((a, b) => {
      const depthA = a.gridCol + this.getFurnitureWidth(a.typeId, a.rotation || 0) + a.gridRow + this.getFurnitureHeight(a.typeId, a.rotation || 0);
      const depthB = b.gridCol + this.getFurnitureWidth(b.typeId, b.rotation || 0) + b.gridRow + this.getFurnitureHeight(b.typeId, b.rotation || 0);
      return depthB - depthA;
    });

    // Pass 1: Floating interactive action badge directly above furniture
    for (const item of placed) {
      const badgeContainer = this.furnitureBadgeContainers.get(item.id);
      if (!badgeContainer) continue;
      const badgeW = (badgeContainer.getData('badgeWidth') as number) || 80;
      const badgeX = badgeContainer.x;
      const badgeY = badgeContainer.y;
      const badgeRect = new Phaser.Geom.Rectangle(badgeX - badgeW / 2 - 4, badgeY - 11, badgeW + 8, 22);
      if (Phaser.Geom.Rectangle.Contains(badgeRect, worldX, worldY)) {
        return item;
      }
    }

    // Pass 2: Exact isometric footprint cell match
    if (tappedCell) {
      const cellMatch = this.getFurnitureAtCell(tappedCell.col, tappedCell.row);
      if (cellMatch) {
        return cellMatch;
      }
    }

    // Pass 3: Scaled visual sprite body (and companion desk chair sprite) above footprint
    for (const item of placed) {
      const sprite = this.furnitureSprites.get(item.id);
      if (sprite) {
        const bounds = this.getSpriteVisualBounds(sprite);
        const centerX = (bounds.left + bounds.right) * 0.5;
        const halfW = Math.max(HomeScene.MIN_FURNITURE_TOUCH_HALF_WIDTH, (bounds.right - bounds.left) * 0.5);
        const bodyRect = new Phaser.Geom.Rectangle(centerX - halfW, bounds.top, halfW * 2, bounds.bottom - bounds.top);
        if (Phaser.Geom.Rectangle.Contains(bodyRect, worldX, worldY)) {
          return item;
        }
      }

      const chairSprite = this.deskChairSprites.get(item.id);
      if (chairSprite) {
        const chairRect = new Phaser.Geom.Rectangle(chairSprite.x - 16, chairSprite.y - 24, 32, 32);
        if (Phaser.Geom.Rectangle.Contains(chairRect, worldX, worldY)) {
          return item;
        }
      }
    }

    return null;
  }

  private setupEventListeners(): void {
    // Clean up any prior listeners if scene restarted
    this.cleanupEventListeners();

    this.unsubscribes.push(
      EventBus.on('FURNITURE_UPDATED', () => {
        this.renderFurniture();
        if (this.isDecorMode) {
          this.renderGridOverlay();
        }
      }),
      EventBus.on('STATE_UNLOCKED', () => {
        this.renderWallDecorations();
        this.renderHomeCozyShelfBar();
      }),
      EventBus.on('STATS_UPDATED', () => {
        const triggeredNewLowAlert = this.syncStatBadgeGates();
        const s = GameState.getState().stats;
        const sig = `${s.energy >= 100 ? 2 : s.energy <= HomeScene.STAT_ALERT_THRESHOLD ? 1 : 0}_${s.hunger >= 100 ? 2 : s.hunger <= HomeScene.STAT_ALERT_THRESHOLD ? 1 : 0}_${s.happiness >= 100 ? 2 : s.happiness <= HomeScene.STAT_ALERT_THRESHOLD ? 1 : 0}_${this.suppressedUntilLowStats.energy ? 1 : 0}${this.suppressedUntilLowStats.hunger ? 1 : 0}${this.suppressedUntilLowStats.happiness ? 1 : 0}_${this.badgesAutoHidden ? 1 : 0}`;
        if (triggeredNewLowAlert || (this as unknown as { _lastBadgeSig?: string })._lastBadgeSig !== sig) {
          (this as unknown as { _lastBadgeSig?: string })._lastBadgeSig = sig;
          this.renderFurniture();
        }
      }),
      EventBus.on('TIME_ADVANCED', (overrideHour?: number) => {
        this.updateWindowLighting(overrideHour);
      }),
      EventBus.on('time:phaseChanged', (payload?: { hour?: number; phase?: 'day' | 'sunset' | 'night' } | number) => {
        if (typeof payload === 'number') {
          this.updateWindowLighting(payload);
        } else if (payload && typeof payload.hour === 'number') {
          this.updateWindowLighting(payload.hour);
        } else if (payload && payload.phase) {
          this.updateWindowLighting(payload.phase === 'sunset' ? 18 : payload.phase === 'night' ? 22 : 10);
        } else {
          this.updateWindowLighting();
        }
      }),
      EventBus.on('TOGGLE_DECOR_MODE', (active: boolean) => {
        this.setDecorMode(active, true);
      }),
      EventBus.on('REQUEST_LEAVE_DECOR_TO_MAP', () => {
        console.log('[EventLog] [Decor Mode] Intercepted Map navigation while in Decor Mode -> Opening Save Confirmation Modal');
        this.openDecorSaveConfirmModal();
      }),
      EventBus.on('NAVIGATE_TO_MAP', () => {
        if (this.isDecorMode) {
          this.setDecorMode(false, true);
          EventBus.emit('DECOR_MODE_CHANGED', false);
        }
        this.destroyHomeCozyShelfBar();
        if (!this.game || this.game.scene.getActiveScene()?.sceneKey !== 'MapScene') {
          this.scene.start('MapScene');
        }
      }),
      EventBus.on('SELECT_FURNITURE_FOR_MOVE', (furnitureId: string) => {
        if (!this.isDecorMode) {
          this.setDecorMode(true);
          EventBus.emit('DECOR_MODE_CHANGED', true);
        }
        this.focusedDecorFurnitureId = furnitureId;
        this.selectedFurnitureForMoveId = furnitureId;
        this.renderFurniture();
        this.renderGridOverlay();
      }),
      EventBus.on('CAMERA_ZOOM_DELTA', (delta: number) => {
        this.setCameraZoom(this.cameras.main.zoom + delta);
      }),
      EventBus.on('CAMERA_RECENTER', () => {
        this.setCameraZoom(HomeScene.DEFAULT_ZOOM);
        this.syncCameraFocusTarget();
        this.cameras.main.startFollow(this.cameraFocusTarget, true, 0.08, 0.08);
        this.cameras.main.pan(this.cameraFocusTarget.x, this.cameraFocusTarget.y, 250, 'Power2');
      }),
      EventBus.on('INTERACT_FURNITURE_TYPE', (typeId: string) => {
        const placed = GameState.getState().placedFurniture || [];
        const targetItem = placed.find((f) => f.typeId === typeId);
        if (targetItem) {
          this.handleFurnitureTap(targetItem);
        } else if (typeId === 'bed') {
          this.openBedModal();
        } else if (typeId === 'fridge') {
          void import('../ui/Modals/PantryStoreModal').then(({ PantryStoreModal }) => {
            PantryStoreModal.getInstance().open();
          });
        } else if (typeId === 'sofa') {
          this.openSofaModal();
        }
      })
    );

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.cleanupEventListeners();
    });
  }

  private cleanupEventListeners(): void {
    for (const unsub of this.unsubscribes) {
      unsub();
    }
    this.unsubscribes = [];
    if (this.isDecorMode) {
      this.isDecorMode = false;
      this.focusedDecorFurnitureId = null;
      this.selectedFurnitureForMoveId = null;
      this.isDraggingFurniture = false;
      this.furnitureDragMoved = false;
      if (DecorSystem.hasActiveDecorSession()) {
        DecorSystem.commitDecorSession();
      }
      EventBus.emit('DECOR_MODE_CHANGED', false);
    }
    for (const badge of this.fadingBadgeContainers) {
      if (!badge.destroyed) {
        badge.destroy();
      }
    }
    this.fadingBadgeContainers.clear();
    this.gridOverlayGraphics = null;
    this.decorActionMenuContainer = null;
    this.decorBannerContainer = null;
    this.destroyDecorFloatingDock();
    this.destroyHomeCozyShelfBar();
  }

  // =========================================================================
  // MILESTONE M4: ISOMETRIC GRID DECOR & BUILD MODE
  // =========================================================================

  public setDecorMode(active: boolean, saveChanges: boolean = true): void {
    this.isDecorMode = active;
    const targetFloorAlpha = active ? 0.28 : 0.14;
    for (const tile of this.floorSprites) {
      if (tile && !tile.destroyed) {
        tile.setAlpha(targetFloorAlpha);
      }
    }
    if (!active) {
      this.focusedDecorFurnitureId = null;
      this.selectedFurnitureForMoveId = null;
      this.isDraggingFurniture = false;
      this.furnitureDragMoved = false;
      if (this.decorActionMenuContainer) {
        this.decorActionMenuContainer.destroy();
        this.decorActionMenuContainer = null;
      }
      if (this.gridOverlayGraphics) {
        this.gridOverlayGraphics.destroy();
        this.gridOverlayGraphics = null;
      }
      if (this.decorBannerContainer) {
        this.decorBannerContainer.destroy();
        this.decorBannerContainer = null;
      }
      this.destroyDecorFloatingDock();
      if (saveChanges) {
        DecorSystem.commitDecorSession();
        this.renderFurniture();
        ToastManager.getInstance().show('Đã lưu bố cục phòng 2.5D!', 'success');
      } else {
        DecorSystem.discardDecorSession();
        this.renderFurniture();
        ToastManager.getInstance().show('Đã hoàn tác các thay đổi trang trí!', 'info');
      }
      this.renderHomeCozyShelfBar();
    } else {
      this.focusedDecorFurnitureId = null;
      this.selectedFurnitureForMoveId = null;
      this.destroyHomeCozyShelfBar();
      if (!DecorSystem.hasActiveDecorSession()) {
        DecorSystem.beginDecorSession();
      }
      this.renderGridOverlay();
      this.renderFurniture();
      ToastManager.getInstance().show(
        'Chế độ Trang trí: Chạm đồ vật để di chuyển ngay, hoặc mở Cửa Hàng!',
        'info'
      );
    }
  }

  /**
   * Renders a compact floating placement hint above the currently selected furniture item in Decor Mode,
   * draws a crisp bounding frame + vertical guide line around the item's `width × height` footprint,
   * and syncs the bottom Floating Glass Decor Dock (#decor-floating-dock) with contextual quick actions.
   */
  private renderDecorActionMenu(): void {
    if (this.decorActionMenuContainer) {
      this.decorActionMenuContainer.destroy();
      this.decorActionMenuContainer = null;
    }

    if (this.isDecorMode) {
      this.renderDecorFloatingDock();
    } else {
      this.destroyDecorFloatingDock();
      return;
    }

    const activeId = this.selectedFurnitureForMoveId || this.focusedDecorFurnitureId;
    if (!activeId) {
      return;
    }

    const placed = GameState.getState().placedFurniture || [];
    const item = placed.find((f) => f.id === activeId);
    if (!item) {
      this.focusedDecorFurnitureId = null;
      this.selectedFurnitureForMoveId = null;
      this.renderDecorFloatingDock();
      return;
    }

    const footprint = this.getFurnitureFootprintCenter(item);
    const { width, height } = footprint;
    const sprite = this.furnitureSprites.get(item.id);
    const anchorX = sprite ? sprite.x : footprint.x;
    const anchorY = sprite ? sprite.y : footprint.y;

    const labelStr = `📍 Ô (${item.gridCol}, ${item.gridRow}) [${width}×${height}] • Chạm ô xanh hoặc kéo để đặt`;
    const pillW = Math.max(216, labelStr.length * 4.5 + 14);
    const halfPillW = pillW / 2;
    const menuX = Phaser.Math.Clamp(anchorX, halfPillW + 6, 390 - halfPillW - 6);
    const topClearance = item.typeId === 'fridge' ? 60 : item.typeId === 'souvenir' ? 52 : 48;
    const menuY = Math.max(82, anchorY - topClearance);

    const topPt = IsometricEngine.gridToScreen(item.gridCol, item.gridRow);
    const rightPt = IsometricEngine.gridToScreen(item.gridCol + width, item.gridRow);
    const botPt = IsometricEngine.gridToScreen(item.gridCol + width, item.gridRow + height);
    const leftPt = IsometricEngine.gridToScreen(item.gridCol, item.gridRow + height);

    const guideGraphics = this.add.graphics();
    // Outer footprint bounding frame around the entire width x height block under the item
    guideGraphics.lineStyle(2.4, 0xfef08a, 0.96);
    guideGraphics.beginPath();
    guideGraphics.moveTo(topPt.isoX, topPt.isoY);
    guideGraphics.lineTo(rightPt.isoX, rightPt.isoY);
    guideGraphics.lineTo(botPt.isoX, botPt.isoY);
    guideGraphics.lineTo(leftPt.isoX, leftPt.isoY);
    guideGraphics.closePath();
    guideGraphics.strokePath();

    // Vertical guide line (đường gióng) connecting footprint center to floating coordinate badge
    guideGraphics.lineStyle(1.4, 0x38bdf8, 0.85);
    guideGraphics.beginPath();
    guideGraphics.moveTo(footprint.x, footprint.y);
    guideGraphics.lineTo(menuX, menuY + 10);
    guideGraphics.strokePath();
    guideGraphics.fillStyle(0xfef08a, 1.0);
    guideGraphics.fillCircle(footprint.x, footprint.y, 2.6);

    const hintBg = this.add.rectangle(menuX, menuY, pillW, 20, 0x065f46, 0.95)
      .setStrokeStyle(1.4, 0xfacc15, 0.98);
    const hintText = this.add.text(menuX, menuY, labelStr, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '9px',
      fontStyle: 'bold',
      color: '#fef9c3'
    }).setOrigin(0.5, 0.5);

    this.decorActionMenuContainer = this.add.container(0, 0, [guideGraphics, hintBg, hintText]);
    this.decorActionMenuContainer.setDepth(60000);
  }

  /**
   * Prompts the player whether to save or discard their Decor Mode changes before switching to MapScene.
   */
  public openDecorSaveConfirmModal(): void {
    const modalContainer = document.getElementById('modal-container');
    const modalPortal = document.getElementById('modal-portal');
    if (!modalContainer || !modalPortal) return;

    const hasChanges = DecorSystem.hasUnsavedChanges();

    const closeModal = () => {
      cancelPendingScrollRestores();
      modalContainer.classList.remove('active');
      modalContainer.setAttribute('aria-hidden', 'true');
      modalPortal.innerHTML = '';
    };

    preserveScrollPositions(
      modalPortal,
      () => {
        modalPortal.innerHTML = `
      <div class="ui-modal-card ui-modal-card--parchment" style="
        max-width: 358px;
        gap: 14px;
      ">
        <div class="ui-modal-header">
          <h3 class="ui-modal-title" style="font-size: 15px; color: #3B2314; display: inline-flex; align-items: center; gap: 7px;">
            ${renderUiGouacheIcon('icon_decor_palette', { size: 22, className: 'modal-title-gouache-icon' })}
            <span>Bạn đang ở Chế độ Trang trí!</span>
          </h3>
          <button type="button" id="decor-confirm-close" class="ui-modal-close">✕</button>
        </div>

        <p style="font-size: 12.5px; color: #5C3D2E; margin: 0; line-height: 1.48; font-weight: 600;">
          ${
            hasChanges
              ? 'Bạn có thay đổi trong lúc trang trí phòng. Bạn có muốn <strong style="color:#3B2314;">lưu lại thay đổi</strong> trước khi mở <strong style="color:#3B2314;">Bản đồ Mỹ</strong> không?'
              : 'Bạn đang bật Chế độ Trang trí. Bạn có muốn <strong style="color:#3B2314;">lưu bố cục phòng hiện tại</strong> và chuyển sang <strong style="color:#3B2314;">Bản đồ Mỹ</strong> không?'
          }
        </p>

        <div style="display: flex; flex-direction: column; gap: 9px; margin-top: 2px;">
          <button type="button" id="btn-decor-save-map" style="
            background: linear-gradient(180deg, #B8E6C9 0%, #81B29A 55%, #659B80 100%);
            border: 2px solid #3B2314;
            border-radius: 13px;
            padding: 10px 14px;
            color: #23140C;
            font-size: 12.5px;
            font-weight: 900;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
            box-shadow: 0 3px 0 #3B2314, inset 0 1.5px 0 rgba(255, 255, 255, 0.72);
          ">
            ${renderUiGouacheIcon('icon_check_seal', { size: 17 })}
            <span>Lưu thay đổi & Mở Bản đồ</span>
          </button>

          <button type="button" id="btn-decor-discard-map" style="
            background: linear-gradient(180deg, #FAD4C4 0%, #EC9A80 55%, #DC7A5E 100%);
            border: 2px solid #3B2314;
            border-radius: 13px;
            padding: 10px 14px;
            color: #2D160D;
            font-size: 12.5px;
            font-weight: 800;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 7px;
            box-shadow: 0 3px 0 #3B2314, inset 0 1.5px 0 rgba(255, 255, 255, 0.65);
          ">
            ${renderUiGouacheIcon('icon_undo_arrow', { size: 17 })}
            <span>Không lưu (Hoàn tác) & Mở Bản đồ</span>
          </button>

          <button type="button" id="btn-decor-stay" style="
            background: linear-gradient(180deg, #FFFDF7 0%, #F5E6C8 100%);
            border: 2px solid #3B2314;
            border-radius: 13px;
            padding: 10px 14px;
            color: #3B2314;
            font-size: 12px;
            font-weight: 800;
            cursor: pointer;
            box-shadow: 0 2.5px 0 #3B2314;
          ">
            Ở lại tiếp tục Trang trí
          </button>
        </div>
      </div>
    `;

        modalContainer.classList.add('active');
        modalContainer.setAttribute('aria-hidden', 'false');
        preventButtonFocusScroll(modalPortal);
      },
      { isFreshModalOpen: true }
    );

    document.getElementById('decor-confirm-close')?.addEventListener('click', closeModal);
    document.getElementById('btn-decor-stay')?.addEventListener('click', closeModal);

    document.getElementById('btn-decor-save-map')?.addEventListener('click', () => {
      closeModal();
      this.setDecorMode(false, true);
      EventBus.emit('DECOR_MODE_CHANGED', false);
      EventBus.emit('NAVIGATE_TO_MAP');
    });

    document.getElementById('btn-decor-discard-map')?.addEventListener('click', () => {
      closeModal();
      this.setDecorMode(false, false);
      EventBus.emit('DECOR_MODE_CHANGED', false);
      EventBus.emit('NAVIGATE_TO_MAP');
    });
  }

  private renderGridOverlay(): void {
    if (!this.gridOverlayGraphics) {
      this.gridOverlayGraphics = this.add.graphics();
      this.gridOverlayGraphics.setDepth(IsometricEngine.calculateDepth(7, 7, 5));
    }
    const g = this.gridOverlayGraphics;
    g.clear();

    const placed = GameState.getState().placedFurniture || [];
    const activeId = this.selectedFurnitureForMoveId || this.focusedDecorFurnitureId;
    const selectedItem = activeId ? placed.find((f) => f.id === activeId) : undefined;

    const selectedFootprintCells = new Set<string>();
    if (selectedItem) {
      const selW = this.getFurnitureWidth(selectedItem.typeId, selectedItem.rotation || 0);
      const selH = this.getFurnitureHeight(selectedItem.typeId, selectedItem.rotation || 0);
      for (let c = selectedItem.gridCol; c < selectedItem.gridCol + selW; c++) {
        for (let r = selectedItem.gridRow; r < selectedItem.gridRow + selH; r++) {
          selectedFootprintCells.add(`${c},${r}`);
        }
      }
    }

    const otherOccupiedCells = this.buildObstacleSet(activeId || undefined);

    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        const pt = IsometricEngine.gridToScreen(col, row);
        const cellKey = `${col},${row}`;
        const isSelectedFootprint = selectedFootprintCells.has(cellKey);
        const isOtherOccupied = otherOccupiedCells.has(cellKey);

        if (isSelectedFootprint) {
          // Highlight the active/dragged furniture's current cells in bright Golden-Yellow + Cyan inner ring
          g.lineStyle(2.4, 0xfef08a, 1.0);
          g.fillStyle(0xfacc15, 0.48);

          g.beginPath();
          g.moveTo(pt.isoX, pt.isoY);
          g.lineTo(pt.isoX + 20, pt.isoY + 10);
          g.lineTo(pt.isoX, pt.isoY + 20);
          g.lineTo(pt.isoX - 20, pt.isoY + 10);
          g.closePath();
          g.fillPath();
          g.strokePath();

          // Inner double-frame diamond & center marker so user sees exact footprint cells
          g.lineStyle(1.4, 0x38bdf8, 0.95);
          g.beginPath();
          g.moveTo(pt.isoX, pt.isoY + 3);
          g.lineTo(pt.isoX + 14, pt.isoY + 10);
          g.lineTo(pt.isoX, pt.isoY + 17);
          g.lineTo(pt.isoX - 14, pt.isoY + 10);
          g.closePath();
          g.strokePath();

          g.fillStyle(0xfef08a, 0.95);
          g.fillCircle(pt.isoX, pt.isoY + 10, 2.2);
        } else if (isOtherOccupied) {
          // Occupied by another furniture item
          g.lineStyle(1.4, 0xf87171, 0.85);
          g.fillStyle(0xef4444, 0.24);

          g.beginPath();
          g.moveTo(pt.isoX, pt.isoY);
          g.lineTo(pt.isoX + 20, pt.isoY + 10);
          g.lineTo(pt.isoX, pt.isoY + 20);
          g.lineTo(pt.isoX - 20, pt.isoY + 10);
          g.closePath();
          g.fillPath();
          g.strokePath();

          if (!activeId) {
            // Subtle inner amber base rim when no item is selected yet so each furniture footprint is visible
            g.lineStyle(1.1, 0xf59e0b, 0.72);
            g.beginPath();
            g.moveTo(pt.isoX, pt.isoY + 2.5);
            g.lineTo(pt.isoX + 15, pt.isoY + 10);
            g.lineTo(pt.isoX, pt.isoY + 17.5);
            g.lineTo(pt.isoX - 15, pt.isoY + 10);
            g.closePath();
            g.strokePath();
          }
        } else {
          // Empty valid placement cell
          g.lineStyle(1.1, 0x34d399, 0.78);
          g.fillStyle(0x10b981, 0.16);

          g.beginPath();
          g.moveTo(pt.isoX, pt.isoY);
          g.lineTo(pt.isoX + 20, pt.isoY + 10);
          g.lineTo(pt.isoX, pt.isoY + 20);
          g.lineTo(pt.isoX - 20, pt.isoY + 10);
          g.closePath();
          g.fillPath();
          g.strokePath();
        }
      }
    }

    // Draw outer perimeter footprint frame around the selected item (or amber outlines around all placed items when none selected)
    if (selectedItem) {
      const selW = this.getFurnitureWidth(selectedItem.typeId, selectedItem.rotation || 0);
      const selH = this.getFurnitureHeight(selectedItem.typeId, selectedItem.rotation || 0);
      const topPt = IsometricEngine.gridToScreen(selectedItem.gridCol, selectedItem.gridRow);
      const rightPt = IsometricEngine.gridToScreen(selectedItem.gridCol + selW, selectedItem.gridRow);
      const botPt = IsometricEngine.gridToScreen(selectedItem.gridCol + selW, selectedItem.gridRow + selH);
      const leftPt = IsometricEngine.gridToScreen(selectedItem.gridCol, selectedItem.gridRow + selH);

      g.lineStyle(2.6, 0x38bdf8, 0.98);
      g.beginPath();
      g.moveTo(topPt.isoX, topPt.isoY);
      g.lineTo(rightPt.isoX, rightPt.isoY);
      g.lineTo(botPt.isoX, botPt.isoY);
      g.lineTo(leftPt.isoX, leftPt.isoY);
      g.closePath();
      g.strokePath();
    } else {
      for (const item of placed) {
        const w = this.getFurnitureWidth(item.typeId, item.rotation || 0);
        const h = this.getFurnitureHeight(item.typeId, item.rotation || 0);
        const topPt = IsometricEngine.gridToScreen(item.gridCol, item.gridRow);
        const rightPt = IsometricEngine.gridToScreen(item.gridCol + w, item.gridRow);
        const botPt = IsometricEngine.gridToScreen(item.gridCol + w, item.gridRow + h);
        const leftPt = IsometricEngine.gridToScreen(item.gridCol, item.gridRow + h);

        g.lineStyle(1.6, 0xfbbf24, 0.78);
        g.beginPath();
        g.moveTo(topPt.isoX, topPt.isoY);
        g.lineTo(rightPt.isoX, rightPt.isoY);
        g.lineTo(botPt.isoX, botPt.isoY);
        g.lineTo(leftPt.isoX, leftPt.isoY);
        g.closePath();
        g.strokePath();
      }
    }
  }

  private destroyDecorFloatingDock(): void {
    if (typeof document === 'undefined') return;
    const existing = document.getElementById('decor-floating-dock');
    if (existing) {
      existing.remove();
    }
  }

  // =========================================================================
  // COZY 5-STATE BADGE SHELF & QUICK ROOM ACTION BAR (FILL LOWER SPACE)
  // =========================================================================

  private destroyHomeCozyShelfBar(): void {
    if (typeof document === 'undefined') return;
    const existing = document.getElementById('home-cozy-shelf-bar');
    if (existing) {
      existing.remove();
    }
  }

  /**
   * Renders the cozy 5-State Souvenir Badge Shelf & Quick Room Action Bar (#home-cozy-shelf-bar)
   * in the lower space between the 3D Diorama Pedestal and the bottom navigation controls.
   */
  private renderHomeCozyShelfBar(): void {
    if (typeof document === 'undefined') return;
    const appShell =
      document.getElementById('app-shell') ||
      document.getElementById('game-container')?.parentElement ||
      document.body;
    if (!appShell || this.isDecorMode) {
      this.destroyHomeCozyShelfBar();
      return;
    }

    let shelf = document.getElementById('home-cozy-shelf-bar');
    if (!shelf) {
      shelf = document.createElement('div');
      shelf.id = 'home-cozy-shelf-bar';
      shelf.className = 'home-cozy-shelf-bar';
      appShell.appendChild(shelf);

      const shieldDomEvent = (ev: Event) => {
        if (typeof window !== 'undefined') {
          (window as unknown as { __lastDomUiEventTime?: number }).__lastDomUiEventTime = Date.now();
        }
        ev.stopPropagation();
      };
      shelf.addEventListener('pointerdown', shieldDomEvent);
      shelf.addEventListener('pointerup', shieldDomEvent);
      shelf.addEventListener('mousedown', shieldDomEvent);
      shelf.addEventListener('mouseup', shieldDomEvent);
      shelf.addEventListener('touchstart', shieldDomEvent, { passive: true });
      shelf.addEventListener('touchend', shieldDomEvent, { passive: true });
    }

    const state = GameState.getState();
    const rawUnlocked = (state as unknown as { unlockedStates?: string[] }).unlockedStates;
    const unlockedKeys = Array.isArray(rawUnlocked)
      ? rawUnlocked
      : Object.keys(state.states || { NY: true });
    const placedCount = (state.placedFurniture || []).length;

    const flagshipOrder = [
      { id: 'new_york', code: 'NY', label: 'New York', emoji: '🗽' },
      { id: 'california', code: 'CA', label: 'California', emoji: '🌉' },
      { id: 'texas', code: 'TX', label: 'Texas', emoji: '🤠' },
      { id: 'florida', code: 'FL', label: 'Florida', emoji: '🌴' },
      { id: 'washington', code: 'WA', label: 'Washington', emoji: '🌲' },
    ];

    const isFlagshipUnlocked = (st: { id: string; code: string }) =>
      unlockedKeys.includes(st.id) ||
      unlockedKeys.includes(st.code) ||
      Boolean(state.states?.[st.code]);

    const unlockedCount = flagshipOrder.filter(isFlagshipUnlocked).length;

    const extraAtlasSouvenirsCount = getAllSouvenirEntries().filter(
      (entry) => !entry.isFlagship && isSouvenirUnlocked(entry.id, state.inventory?.unlockedSouvenirs)
    ).length;

    const statePillsHtml = flagshipOrder
      .map((st) => {
        const isUnlocked = isFlagshipUnlocked(st);
        const souvImg = renderSouvenirVisual(st.code, {
          size: 14,
          idSuffix: `shelf_${st.code}`,
          className: 'cozy-state-badge-img',
          title: st.label,
        });
        return `
          <button
            type="button"
            class="cozy-state-badge-pill ${isUnlocked ? 'is-unlocked' : 'is-locked'}"
            data-state-id="${st.id}"
            title="${st.label} (${isUnlocked ? 'Đã mở khóa' : 'Chưa mở khóa'})"
          >
            <span class="cozy-state-badge-emoji">${souvImg}</span>
            <span class="cozy-state-badge-code">${st.code}</span>
            <span class="cozy-state-badge-dot">${
              isUnlocked
                ? '★'
                : renderUiGouacheIcon('icon_lock_padlock', {
                    size: 11,
                    className: 'cozy-badge-lock-img',
                  })
            }</span>
          </button>
        `;
      })
      .join('');

    const extraAtlasChipHtml =
      extraAtlasSouvenirsCount > 0
        ? `<button type="button" id="btn-shelf-open-souvenirs" class="cozy-atlas-souvenir-chip" title="Mở bộ sưu tập Kỷ vật Bang">+${extraAtlasSouvenirsCount}</button>`
        : '';

    const quickBedIcon = renderDecorFurnitureVisual('bed', 1, {
      size: 17,
      idSuffix: 'quick_bed',
      className: 'cozy-quick-chip-img',
    });
    const quickFridgeIcon = renderDecorFurnitureVisual('fridge', 1, {
      size: 17,
      idSuffix: 'quick_fridge',
      className: 'cozy-quick-chip-img',
    });
    const quickDeskIcon = renderDecorFurnitureVisual('desk', 1, {
      size: 17,
      idSuffix: 'quick_desk',
      className: 'cozy-quick-chip-img',
    });
    const quickSofaIcon = renderDecorFurnitureVisual('sofa', 1, {
      size: 17,
      idSuffix: 'quick_sofa',
      className: 'cozy-quick-chip-img',
    });

    shelf.innerHTML = `
      <div class="cozy-shelf-top-row">
        <div class="cozy-shelf-room-tag">
          ${renderUiGouacheIcon('icon_home_cottage', { size: 16, className: 'cozy-room-cottage-icon' })}
          <span>Phòng 2.5D · <strong>${placedCount}</strong> đồ</span>
        </div>
        <div class="cozy-shelf-quick-actions">
          <button type="button" class="cozy-quick-chip" data-quick-furniture="bed" title="Đi tới Giường ngủ">${quickBedIcon}<span>Ngủ</span></button>
          <button type="button" class="cozy-quick-chip" data-quick-furniture="fridge" title="Mở Tủ lạnh">${quickFridgeIcon}<span>Ăn</span></button>
          <button type="button" class="cozy-quick-chip" data-quick-furniture="desk" title="Bàn PC Làm việc">${quickDeskIcon}<span>Làm</span></button>
          <button type="button" class="cozy-quick-chip" data-quick-furniture="sofa" title="Ghế Sofa Thư giãn">${quickSofaIcon}<span>Chill</span></button>
        </div>
      </div>
      <div class="cozy-shelf-badges-row">
        <span class="cozy-shelf-label">${renderUiGouacheIcon('icon_trophy_medal', {
          size: 14,
          className: 'cozy-shelf-trophy-img',
        })}<span class="ui-sr-legacy-token">🏆 </span><span>HUY HIỆU (${unlockedCount}/5):</span></span>
        <div class="cozy-shelf-badges-list">
          ${statePillsHtml}
          ${extraAtlasChipHtml}
        </div>
      </div>
    `;

    // Wire quick furniture chips
    shelf.querySelectorAll<HTMLButtonElement>('[data-quick-furniture]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const fType = btn.getAttribute('data-quick-furniture');
        if (fType) {
          EventBus.emit('INTERACT_FURNITURE_TYPE', fType);
        }
      });
    });

    // Wire state badge pills -> navigate to Map and open StateInspectModal on Souvenir tab
    shelf.querySelectorAll<HTMLButtonElement>('[data-state-id]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const stId = btn.getAttribute('data-state-id');
        if (stId) {
          EventBus.emit('NAVIGATE_TO_MAP');
          setTimeout(() => {
            EventBus.emit('OPEN_STATE_INSPECT', {
              stateId: stId,
              initialTab: 'souvenir',
            });
          }, 80);
        }
      });
    });

    shelf.querySelector<HTMLButtonElement>('#btn-shelf-open-souvenirs')?.addEventListener('click', () => {
      DecorCatalogModal.getInstance().open(undefined, 'souvenirs');
    });
  }

  /**
   * Renders the ergonomic bottom Floating Storybook Gouache Decor Dock (#decor-floating-dock) in the thumb zone,
   * including:
   * 1. Contextual Selected-Item Quick-Action Bar (#decor-item-action-bar) with 1-Tap Direct Upgrade, Rotate 90°, Store, and Deselect.
   * 2. Main Decor Toolbar (#decor-main-toolbar) with [Cửa Hàng], [Hoàn tác], and [Lưu & Xong].
   */
  private renderDecorFloatingDock(): void {
    if (typeof document === 'undefined') return;
    if (!this.isDecorMode) {
      this.destroyDecorFloatingDock();
      return;
    }

    let dock = document.getElementById('decor-floating-dock');
    if (!dock) {
      dock = document.createElement('div');
      dock.id = 'decor-floating-dock';
      dock.className = 'decor-floating-dock';
      const parent =
        document.getElementById('app-shell') ||
        document.getElementById('game-container')?.parentElement ||
        document.body;
      parent.appendChild(dock);

      const shieldDomEvent = (ev: Event) => {
        if (typeof window !== 'undefined') {
          (window as unknown as { __lastDomUiEventTime?: number }).__lastDomUiEventTime = Date.now();
        }
        ev.stopPropagation();
      };
      dock.addEventListener('pointerdown', shieldDomEvent);
      dock.addEventListener('pointerup', shieldDomEvent);
      dock.addEventListener('mousedown', shieldDomEvent);
      dock.addEventListener('mouseup', shieldDomEvent);
      dock.addEventListener('touchstart', shieldDomEvent, { passive: true });
      dock.addEventListener('touchend', shieldDomEvent, { passive: true });
    }

    const state = GameState.getState();
    const placed = state.placedFurniture || [];
    const activeId = this.selectedFurnitureForMoveId || this.focusedDecorFurnitureId;
    const selectedItem = activeId ? placed.find((f) => f.id === activeId) : undefined;

    let itemBarHtml = '';
    if (selectedItem) {
      const isSouvenir = selectedItem.typeId === 'souvenir';
      const selW = this.getFurnitureWidth(selectedItem.typeId, selectedItem.rotation || 0);
      const selH = this.getFurnitureHeight(selectedItem.typeId, selectedItem.rotation || 0);
      const tierInfo = FURNITURE_CATALOG_ITEMS.find(
        (c) => c.typeId === selectedItem.typeId && c.tier === selectedItem.tier
      );
      const nextTierInfo = !isSouvenir
        ? FURNITURE_CATALOG_ITEMS.find(
            (c) => c.typeId === selectedItem.typeId && c.tier === selectedItem.tier + 1
          )
        : undefined;

      let title = tierInfo?.name || selectedItem.typeId;
      let iconVisual = renderDecorFurnitureVisual(selectedItem.typeId, selectedItem.tier || 1, {
        size: 20,
        idSuffix: `dock_${selectedItem.id}`,
        title,
      });
      if (isSouvenir && selectedItem.customData?.souvenirId) {
        const rawSouvId = selectedItem.customData.souvenirId;
        const souvEntry = getSouvenirEntry(rawSouvId);
        title = souvEntry ? souvEntry.name : 'Kỷ vật Tiểu bang';
        iconVisual = renderSouvenirVisual(rawSouvId, {
          size: 20,
          idSuffix: `dock_souv_${selectedItem.id}`,
          title,
        });
      }

      const canAffordUpgrade = nextTierInfo ? state.stats.money >= nextTierInfo.costUSD : false;
      const upgradeLabel = isSouvenir
        ? `${renderUiGouacheIcon('icon_trophy_medal', { size: 15 })} <span>Kỷ vật</span>`
        : nextTierInfo
        ? `${renderUiGouacheIcon('icon_trophy_medal', { size: 15 })} <span>Lên T${nextTierInfo.tier} ($${nextTierInfo.costUSD})</span>`
        : `${renderUiGouacheIcon('icon_trophy_medal', { size: 15 })} <span>Tối đa</span>`;

      itemBarHtml = `
        <div id="decor-item-action-bar" class="decor-item-action-bar">
          <div class="decor-item-header">
            <span class="decor-item-title"><span style="display:inline-flex;align-items:center;justify-content:center;width:25px;height:25px;border-radius:7px;background:#FFF8E7;border:1.8px solid #3B2314;box-shadow:0 1.5px 0 #3B2314;flex-shrink:0;">${iconVisual}</span> <strong>${title}</strong> ${
              !isSouvenir ? `<span class="decor-item-tier">T${selectedItem.tier}</span>` : ''
            }</span>
            <span class="decor-item-coords">Ô (${selectedItem.gridCol}, ${selectedItem.gridRow}) [${selW}×${selH}] · Chạm ô xanh để đặt</span>
          </div>
          <div class="decor-quick-actions-row">
            <button id="btn-decor-quick-rotate" class="decor-quick-btn" type="button" title="Xoay 90 độ">
              ${renderUiGouacheIcon('icon_rotate_turn', { size: 15 })} <span>Xoay 90°</span>
            </button>
            <button id="btn-decor-quick-upgrade" class="decor-quick-btn decor-quick-btn--upgrade" type="button" ${
              !nextTierInfo || !canAffordUpgrade ? 'disabled' : ''
            }>
              ${upgradeLabel}
            </button>
            <button id="btn-decor-quick-remove" class="decor-quick-btn decor-quick-btn--danger" type="button">
              ${renderUiGouacheIcon('icon_storage_box', { size: 15 })} <span>Cất đồ</span>
            </button>
            <button id="btn-decor-quick-deselect" class="decor-quick-btn decor-quick-btn--done" type="button">
              ${renderUiGouacheIcon('icon_check_seal', { size: 15 })} <span>Đặt xong</span>
            </button>
          </div>
        </div>
      `;
    }

    const dockEl = dock;
    preserveScrollPositions(dockEl, () => {
      dockEl.innerHTML = `
      ${itemBarHtml}
      <div id="decor-main-toolbar" class="decor-main-toolbar">
        <button id="btn-decor-open-shop" class="decor-dock-btn decor-dock-btn--shop" type="button">
          ${renderUiGouacheIcon('icon_shop_basket', { size: 18 })} <span>Cửa Hàng</span>
        </button>
        <button id="btn-decor-undo-cancel" class="decor-dock-btn decor-dock-btn--undo" type="button">
          ${renderUiGouacheIcon('icon_undo_arrow', { size: 18 })} <span>Hoàn tác</span>
        </button>
        <button id="btn-decor-save-done" class="decor-dock-btn decor-dock-btn--save" type="button">
          ${renderUiGouacheIcon('icon_check_seal', { size: 18 })} <span>Lưu & Xong</span>
        </button>
      </div>
    `;
      preventButtonFocusScroll(dockEl);
    });

    // Wire main toolbar events
    dock.querySelector('#btn-decor-open-shop')?.addEventListener('click', () => {
      DecorCatalogModal.getInstance().open(undefined, 'shop');
    });

    dock.querySelector('#btn-decor-undo-cancel')?.addEventListener('click', () => {
      this.setDecorMode(false, false);
      EventBus.emit('DECOR_MODE_CHANGED', false);
    });

    dock.querySelector('#btn-decor-save-done')?.addEventListener('click', () => {
      this.setDecorMode(false, true);
      EventBus.emit('DECOR_MODE_CHANGED', false);
    });

    // Wire contextual selected-item quick actions
    if (selectedItem) {
      dock.querySelector('#btn-decor-quick-rotate')?.addEventListener('click', () => {
        const ok = DecorSystem.rotateFurniture(selectedItem.id);
        if (ok) {
          ToastManager.getInstance().show('Đã xoay nội thất 90°!', 'info');
          this.renderFurniture();
          this.renderGridOverlay();
        } else {
          ToastManager.getInstance().show('Không đủ khoảng trống để xoay tại vị trí này!', 'warning');
        }
      });

      dock.querySelector('#btn-decor-quick-upgrade')?.addEventListener('click', () => {
        const res = DecorSystem.upgradeFurniture(selectedItem.id);
        if (res.success) {
          ToastManager.getInstance().show(`Đã nâng cấp lên Cấp ${res.newTier}! (-$${res.cost})`, 'success');
          this.renderFurniture();
          this.renderGridOverlay();
        } else {
          ToastManager.getInstance().show(res.error || 'Không thể nâng cấp!', 'warning');
        }
      });

      dock.querySelector('#btn-decor-quick-remove')?.addEventListener('click', () => {
        const res = DecorSystem.removeFurniture(selectedItem.id);
        if (res.success) {
          ToastManager.getInstance().show('Đã cất đồ vật vào kho!', 'info');
          this.focusedDecorFurnitureId = null;
          this.selectedFurnitureForMoveId = null;
          this.renderFurniture();
          this.renderGridOverlay();
        } else {
          ToastManager.getInstance().show(
            res.error || 'Vật phẩm cốt lõi cuối cùng trong phòng không thể xóa!',
            'warning'
          );
        }
      });

      dock.querySelector('#btn-decor-quick-deselect')?.addEventListener('click', () => {
        this.focusedDecorFurnitureId = null;
        this.selectedFurnitureForMoveId = null;
        this.renderFurniture();
        this.renderGridOverlay();
      });
    }
  }

  private getFurnitureAtCell(col: number, row: number): PlacedFurniture | null {
    const placed = GameState.getState().placedFurniture || [];
    for (const item of placed) {
      const w = this.getFurnitureWidth(item.typeId, item.rotation || 0);
      const h = this.getFurnitureHeight(item.typeId, item.rotation || 0);
      if (
        col >= item.gridCol && col < item.gridCol + w &&
        row >= item.gridRow && row < item.gridRow + h
      ) {
        return item;
      }
    }
    return null;
  }

  private buildObstacleSet(excludeFurnitureId?: string): Set<string> {
    const obstacles = new Set<string>();
    const placed = GameState.getState().placedFurniture || [];

    for (const item of placed) {
      if (excludeFurnitureId && item.id === excludeFurnitureId) {
        continue;
      }
      const w = this.getFurnitureWidth(item.typeId, item.rotation || 0);
      const h = this.getFurnitureHeight(item.typeId, item.rotation || 0);
      for (let c = item.gridCol; c < item.gridCol + w; c++) {
        for (let r = item.gridRow; r < item.gridRow + h; r++) {
          obstacles.add(`${c},${r}`);
        }
      }
    }

    return obstacles;
  }

  /**
   * Tapping on a floor tile:
   * - In Decor Mode with a selected item in Move Mode: moves the selected furniture to (targetCol, targetRow)
   *   and keeps it selected so the player can keep adjusting, rotating, or upgrading it, or tap [✓ Đặt xong].
   * - In Decor Mode without Move Mode active: deselects any focused furniture menu (or selects furniture on that cell).
   * - In Living Mode: if a furniture item occupies (targetCol, targetRow), interacts with it; otherwise walks to tile.
   */
  public handleTileTap(targetCol: number, targetRow: number): void {
    if (this.isDecorMode) {
      if (this.selectedFurnitureForMoveId) {
        const moved = DecorSystem.moveFurniture(this.selectedFurnitureForMoveId, targetCol, targetRow);
        if (moved) {
          this.focusedDecorFurnitureId = this.selectedFurnitureForMoveId;
          this.renderFurniture();
          this.renderGridOverlay();
        } else {
          // If the tapped cell belongs to another furniture piece, switch focus to that piece
          const otherFurniture = this.getFurnitureAtCell(targetCol, targetRow);
          if (otherFurniture && otherFurniture.id !== this.selectedFurnitureForMoveId) {
            this.handleFurnitureTap(otherFurniture);
          } else {
            ToastManager.getInstance().show('Không thể đặt ở ô này (vướng tường hoặc đồ vật khác)!', 'warning');
          }
        }
      } else if (this.focusedDecorFurnitureId) {
        this.focusedDecorFurnitureId = null;
        this.renderFurniture();
        this.renderGridOverlay();
      }
      return;
    }

    const furnitureOnCell = this.getFurnitureAtCell(targetCol, targetRow);
    if (furnitureOnCell) {
      this.handleFurnitureTap(furnitureOnCell);
      return;
    }

    const obstacles = this.buildObstacleSet();
    if (obstacles.has(`${targetCol},${targetRow}`)) {
      return; // Cell is occupied
    }

    this.walkTo(targetCol, targetRow);
  }

  /**
   * Tapping on a furniture piece:
   * - In Decor Mode: 1-Tap immediately activates Move Mode for that furniture piece and displays the contextual Quick-Action Bar.
   * - In Living Mode: finds adjacent access tile, walks character to it, and triggers interaction upon arrival.
   *   If another interaction is requested while walking, the newest interaction preempts the previous one.
   */
  public handleFurnitureTap(item: PlacedFurniture): void {
    // Visual tap feedback bounce relative to the per-tier furniture scale
    const sprite = this.furnitureSprites.get(item.id);
    if (sprite) {
      const baseScale = this.getFurnitureVisualScale(item.typeId, item.tier || 1);
      sprite.setScale(baseScale);
      this.tweens.add({
        targets: sprite,
        scaleX: baseScale * 1.07,
        scaleY: baseScale * 1.07,
        duration: 90,
        yoyo: true,
        ease: 'Quad.easeOut',
      });
    }

    const chairSprite = this.deskChairSprites.get(item.id);
    if (chairSprite) {
      const chairBaseScale = this.getDeskChairScale(item);
      chairSprite.setScale(chairBaseScale);
      this.tweens.add({
        targets: chairSprite,
        scaleX: chairBaseScale * 1.07,
        scaleY: chairBaseScale * 1.07,
        duration: 90,
        yoyo: true,
        ease: 'Quad.easeOut',
      });
    }

    if (this.isDecorMode) {
      this.focusedDecorFurnitureId = item.id;
      this.selectedFurnitureForMoveId = item.id;
      this.renderFurniture();
      this.renderGridOverlay();
      return;
    }

    const obstacles = this.buildObstacleSet(item.id);
    const footprint = {
      width: this.getFurnitureWidth(item.typeId, item.rotation || 0),
      height: this.getFurnitureHeight(item.typeId, item.rotation || 0),
    };

    const accessTile = Pathfinding.findNearestAccessTile(
      { col: item.gridCol, row: item.gridRow },
      footprint,
      this.playerPos,
      obstacles
    );

    if (!accessTile) {
      this.activeWalkRequestId++;
      this.preemptActiveWalk();
      this.isMoving = false;
      this.targetCursor?.setVisible(false);
      this.snapCharacterToCurrentTile();
      ToastManager.getInstance().show('Lối vào đồ đạc bị chặn!', 'warning');
      this.playBlockedWiggle();
      return;
    }

    // If player is already standing still on the access tile, trigger interaction immediately
    if (!this.isMoving && this.playerPos.col === accessTile.col && this.playerPos.row === accessTile.row) {
      console.log(`[EventLog] [Interact Immediate] Player already at (${accessTile.col},${accessTile.row}) -> Opening "${item.typeId}"`);
      this.triggerFurnitureInteraction(item);
      return;
    }

    console.log(
      `[EventLog] [Walk & Interact] Requesting walk from (${this.playerPos.col},${this.playerPos.row}) to accessTile=(${accessTile.col},${accessTile.row}) for "${item.typeId}"`
    );

    // Walk to access tile; if another interaction is tapped mid-walk, this callback is superseded by the newest one
    this.walkTo(accessTile.col, accessTile.row, () => {
      console.log(`[EventLog] [Arrived at Furniture] Reached (${accessTile.col},${accessTile.row}) -> Opening "${item.typeId}"`);
      this.triggerFurnitureInteraction(item);
    });
  }

  private stopBlockedWiggleTween(): void {
    if (this.blockedWiggleTween) {
      this.blockedWiggleTween.stop();
      this.blockedWiggleTween = null;
    }
  }

  private preemptActiveWalk(targetCol?: number, targetRow?: number, requestId?: number): void {
    if (this.blockedWiggleTween) {
      this.stopBlockedWiggleTween();
      if (!this.moveTween && this.playerSprite) {
        const basePt = IsometricEngine.gridToScreen(this.playerPos.col, this.playerPos.row);
        this.playerSprite.x = basePt.isoX;
        if (this.playerCozySprite) {
          this.playerCozySprite.x = basePt.isoX;
        }
        if (this.shadowSprite) {
          this.shadowSprite.x = basePt.isoX;
        }
      }
    }

    if (this.moveTween) {
      if (requestId !== undefined && targetCol !== undefined && targetRow !== undefined) {
        console.log(
          `[EventLog] [Walk Preempted] Cancelling previous walk request #${requestId - 1} in favor of #${requestId} -> (${targetCol},${targetRow})`
        );
      }
      this.moveTween.stop();
      this.moveTween = null;

      if (this.currentStepTile && this.playerSprite) {
        const curPt = IsometricEngine.gridToScreen(this.playerPos.col, this.playerPos.row);
        const nextPt = IsometricEngine.gridToScreen(this.currentStepTile.col, this.currentStepTile.row);
        const distToCur = Phaser.Math.Distance.Between(
          this.playerSprite.x,
          this.playerSprite.y,
          curPt.isoX,
          curPt.isoY + 10
        );
        const distToNext = Phaser.Math.Distance.Between(
          this.playerSprite.x,
          this.playerSprite.y,
          nextPt.isoX,
          nextPt.isoY + 10
        );
        if (distToNext < distToCur) {
          this.playerPos = { ...this.currentStepTile };
        }
      }
      this.currentStepTile = null;
    }
  }

  private snapCharacterToCurrentTile(): void {
    this.stopBlockedWiggleTween();
    if (!this.playerSprite) return;

    const pt = IsometricEngine.gridToScreen(this.playerPos.col, this.playerPos.row);
    const depth = IsometricEngine.calculateDepth(this.playerPos.col, this.playerPos.row, 20);
    this.playerSprite.setPosition(pt.isoX, pt.isoY + 10).setDepth(depth);
    this.playerSprite.play(`idle_${this.currentDirection}`);

    if (this.shadowSprite) {
      this.shadowSprite
        .setPosition(pt.isoX, pt.isoY + 10 + HomeScene.PLAYER_SHADOW_GROUND_OFFSET_Y)
        .setDepth(depth - 1);
    }

    this.syncCameraFocusTarget();
    this.updateCharacterAnimationFrame(this.atmosphereTimeSec, 0, 0);
  }

  /**
   * Executes A* pathfinding and step-by-step walking animation along the path.
   * Any new call to walkTo preempts an in-flight walk and cancels the older onArrival callback.
   */
  public walkTo(targetCol: number, targetRow: number, onArrival?: () => void): void {
    const requestId = ++this.activeWalkRequestId;

    // Re-attach camera follow to the balanced room+player focus target when player walks
    this.syncCameraFocusTarget();
    this.cameras.main.startFollow(this.cameraFocusTarget, true, 0.08, 0.08);

    // If currently moving mid-step (or wiggling on a blocked tap), stop active tween and snap playerPos to closest tile
    this.preemptActiveWalk(targetCol, targetRow, requestId);

    const obstacles = this.buildObstacleSet();
    const path = Pathfinding.findPath(this.playerPos, { col: targetCol, row: targetRow }, obstacles);

    if (path.length <= 1) {
      this.isMoving = false;
      this.targetCursor.setVisible(false);
      this.snapCharacterToCurrentTile();
      if (path.length === 1 && path[0].col === this.playerPos.col && path[0].row === this.playerPos.row) {
        if (onArrival && requestId === this.activeWalkRequestId) {
          onArrival();
        }
        return;
      }
      this.playBlockedWiggle();
      ToastManager.getInstance().show('Không có lối đi đến vị trí này!', 'warning');
      return;
    }

    // Show target cursor at destination
    const targetPt = IsometricEngine.gridToScreen(targetCol, targetRow);
    this.targetCursor.setPosition(targetPt.isoX, targetPt.isoY).setVisible(true);

    this.isMoving = true;
    this.activePath = path.slice(1); // steps excluding starting cell

    this.executeNextPathStep(requestId, onArrival);
  }

  private executeNextPathStep(requestId: number, onArrival?: () => void): void {
    // Ignore if superseded by a newer walkTo / interaction request
    if (requestId !== this.activeWalkRequestId) {
      return;
    }

    if (this.activePath.length === 0) {
      this.isMoving = false;
      this.moveTween = null;
      this.currentStepTile = null;
      this.targetCursor.setVisible(false);
      this.snapCharacterToCurrentTile();
      if (onArrival && requestId === this.activeWalkRequestId) {
        onArrival();
      }
      return;
    }

    const nextTile = this.activePath.shift()!;
    this.currentStepTile = nextTile;
    const dCol = nextTile.col - this.playerPos.col;
    const dRow = nextTile.row - this.playerPos.row;

    // Determine direction
    if (dCol > 0) this.currentDirection = 'se';
    else if (dCol < 0) this.currentDirection = 'nw';
    else if (dRow > 0) this.currentDirection = 'sw';
    else if (dRow < 0) this.currentDirection = 'ne';

    this.playerSprite.play(`walk_${this.currentDirection}`);
    if (this.playerCozySprite) {
      const isBack = this.currentDirection === 'ne' || this.currentDirection === 'nw';
      const targetKey =
        this.walkCyclePhase > 0
          ? this.resolveWalkCycleTextureKey(isBack)
          : isBack && this.textures.exists('player_cozy_back')
          ? 'player_cozy_back'
          : 'player_cozy';
      if (this.textures.exists(targetKey)) {
        this.playerCozySprite.setTexture(targetKey);
      }
      this.playerCozySprite.setFlipX(this.currentDirection === 'sw' || this.currentDirection === 'nw');
    }

    const nextScreenPt = IsometricEngine.gridToScreen(nextTile.col, nextTile.row);
    const tweenTargets: Phaser.GameObjects.GameObject[] = [this.playerSprite, this.shadowSprite];

    this.moveTween = this.tweens.add({
      targets: tweenTargets,
      x: nextScreenPt.isoX,
      y: (target: Phaser.GameObjects.GameObject) => {
        return target === this.shadowSprite
          ? nextScreenPt.isoY + 10 + HomeScene.PLAYER_SHADOW_GROUND_OFFSET_Y
          : nextScreenPt.isoY + 10;
      },
      duration: 165, // snappy walking speed
      ease: 'Linear',
      onUpdate: () => {
        const depth = IsometricEngine.calculateDepth(nextTile.col, nextTile.row, 20);
        this.playerSprite.setDepth(depth);
        this.shadowSprite.setDepth(depth - 1);
        if (this.playerCozySprite) {
          this.playerCozySprite.x = this.playerSprite.x;
          this.playerCozySprite.setDepth(depth + 0.1);
        }
        this.syncCameraFocusTarget();
      },
      onComplete: () => {
        if (requestId !== this.activeWalkRequestId) {
          return;
        }
        this.moveTween = null;
        this.playerPos = nextTile;
        this.currentStepTile = null;
        this.syncCameraFocusTarget();
        this.executeNextPathStep(requestId, onArrival);
      },
    });
  }

  private playBlockedWiggle(): void {
    this.stopBlockedWiggleTween();
    if (!this.playerSprite) return;

    const baseX = IsometricEngine.gridToScreen(this.playerPos.col, this.playerPos.row).isoX;
    this.playerSprite.x = baseX;
    if (this.playerCozySprite) {
      this.playerCozySprite.x = baseX;
    }
    if (this.shadowSprite) {
      this.shadowSprite.x = baseX;
    }

    const targets: Phaser.GameObjects.GameObject[] = [this.playerSprite];
    if (this.playerCozySprite) {
      targets.push(this.playerCozySprite);
    }
    if (this.shadowSprite) {
      targets.push(this.shadowSprite);
    }

    this.blockedWiggleTween = this.tweens.add({
      targets,
      x: baseX + 4,
      duration: 60,
      yoyo: true,
      repeat: 2,
      onComplete: () => {
        if (this.playerSprite) {
          this.playerSprite.x = baseX;
        }
        if (this.playerCozySprite) {
          this.playerCozySprite.x = baseX;
        }
        if (this.shadowSprite) {
          this.shadowSprite.x = baseX;
        }
        this.blockedWiggleTween = null;
      },
    });
  }

  private showCharacterStatusBubble(text: string, borderColor: number = 0x38bdf8): void {
    if (!this.playerSprite) return;
    const x = this.playerSprite.x;
    const y = this.playerSprite.y - 28;

    const bubbleWidth = Math.max(80, text.length * 5.8 + 18);
    const bubbleHeight = 21;
    const halfW = bubbleWidth / 2;
    const halfH = bubbleHeight / 2;
    const radius = 10.5;

    const bg = this.add.graphics();
    // 1. Chunky 2.5D bottom extrusion shadow (#13101d)
    bg.fillStyle(0x13101d, 0.96);
    bg.fillRoundedRect(-halfW, -halfH + 2.5, bubbleWidth, bubbleHeight, radius);
    bg.fillTriangle(-4.5, halfH + 1.5, 4.5, halfH + 1.5, 0, halfH + 6.5);

    // 2. Main cel-shaded capsule body + downward pointer
    bg.fillStyle(0x0f172a, 0.96);
    bg.fillRoundedRect(-halfW, -halfH, bubbleWidth, bubbleHeight, radius);
    bg.fillTriangle(-3.5, halfH - 1, 3.5, halfH - 1, 0, halfH + 4);

    // 3. Top inner specular gloss strip
    bg.fillStyle(0xffffff, 0.16);
    bg.fillRoundedRect(-halfW + 4, -halfH + 2, bubbleWidth - 8, 5, 3);

    // 4. Inner vibrant semantic rim + crisp outer #13101d cel-shaded contour
    bg.lineStyle(1.5, borderColor, 0.98);
    bg.strokeRoundedRect(-halfW + 0.8, -halfH + 0.8, bubbleWidth - 1.6, bubbleHeight - 1.6, radius - 0.8);
    bg.lineStyle(1.8, 0x13101d, 0.98);
    bg.strokeRoundedRect(-halfW, -halfH, bubbleWidth, bubbleHeight, radius);

    const label = this.add.text(0, 0, text, {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '10px',
      fontStyle: 'bold',
      color: '#ffffff',
    }).setOrigin(0.5, 0.5);

    const container = this.add.container(x, y, [bg, label]);
    container.setDepth(60000);

    this.tweens.add({
      targets: container,
      y: y - 22,
      alpha: 0,
      duration: 1400,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        container.destroy();
      },
    });
  }

  // =========================================================================
  // DIRECT TOUCH INTERACTION MODALS
  // =========================================================================

  private triggerFurnitureInteraction(item: PlacedFurniture): void {
    console.log(`[EventLog] [Modal Open] Triggering interaction for furniture "${item.typeId}" (${item.id})`);
    switch (item.typeId) {
      case 'bed':
        this.startCharacterActivity('sleep', 4800);
        this.openBedModal();
        break;
      case 'sofa':
        this.startCharacterActivity('chill', 4800);
        this.openSofaModal();
        break;
      case 'fridge':
        this.startCharacterActivity('eat', 4200);
        void import('../ui/Modals/PantryStoreModal').then(({ PantryStoreModal }) => {
          PantryStoreModal.getInstance().open();
        });
        break;
      case 'desk':
        this.startCharacterActivity('work', 4800);
        void import('../ui/Modals/CareerHubModal').then(({ CareerHubModal }) => {
          CareerHubModal.getInstance().open();
        });
        break;
      case 'plant':
        this.startCharacterActivity('chill', 3000);
        ToastManager.getInstance().show('Cây xanh tươi mát hồi phục +2% Tinh thần!', 'success');
        GameState.getSurvivalEngine().applyStatDelta({ happiness: 2 });
        this.showCharacterStatusBubble('🪴 +2% Tâm trạng', 0x22c55e);
        break;
      case 'souvenir': {
        this.startCharacterActivity('chill', 3200);
        const souvEntry = getSouvenirEntry(
          item.customData?.souvenirId || 'souvenir_ny_statue_liberty'
        );
        const prefix = souvEntry ? `${souvEntry.vietnameseName} • ` : '';
        ToastManager.getInstance().show(
          `${prefix}Kỷ vật bang truyền cảm hứng giấc mơ Mỹ! +3% Tinh thần`,
          'success'
        );
        GameState.getSurvivalEngine().applyStatDelta({ happiness: 3 });
        this.showCharacterStatusBubble('🗽 +3% Tâm trạng', 0xfbbf24);
        break;
      }
      default:
        break;
    }
  }

  /**
   * Opens Bed Sleep/Nap Modal restoring Energy (and offering quick food options when hungry).
   */
  public openBedModal(): void {
    const modalContainer = document.getElementById('modal-container');
    const modalPortal = document.getElementById('modal-portal');
    if (!modalContainer || !modalPortal) return;

    const stats = GameState.getState().stats;
    const bonuses = DecorSystem.getActiveFurnitureBonuses();
    const curEnergy = Math.round(stats.energy);
    const curHunger = Math.round(stats.hunger);
    const isTired = curEnergy <= 45;
    const isHungry = curHunger <= 45;

    let statusAlertHtml = '';
    if (isTired && isHungry) {
      statusAlertHtml = `
        <div style="background: #FDE68A; border: 1.8px solid #3B2314; box-shadow: 0 2px 0 #3B2314; border-radius: 11px; padding: 8px 11px; margin-bottom: 12px; font-size: 11.5px; color: #3B2314; line-height: 1.42;">
          <strong>Bạn đang vừa Đói (${curHunger}%) vừa Mệt (${curEnergy}%)!</strong> Hãy dùng <em>"Nằm nghỉ lưng (0% hao Đói)"</em> hoặc ăn nhẹ ngay trên giường trước khi ngủ sâu nhé.
        </div>
      `;
    } else if (isTired) {
      statusAlertHtml = `
        <div style="background: #FAD4C4; border: 1.8px solid #3B2314; box-shadow: 0 2px 0 #3B2314; border-radius: 11px; padding: 8px 11px; margin-bottom: 12px; font-size: 11.5px; color: #3B2314; line-height: 1.42;">
          <strong>Cơ thể đang mệt mỏi (${curEnergy}% Năng lượng)!</strong> Hãy chợp mắt hoặc ngủ sâu để lấy lại sức.
        </div>
      `;
    } else if (isHungry) {
      statusAlertHtml = `
        <div style="background: #FCE3B4; border: 1.8px solid #3B2314; box-shadow: 0 2px 0 #3B2314; border-radius: 11px; padding: 8px 11px; margin-bottom: 12px; font-size: 11.5px; color: #3B2314; line-height: 1.42;">
          <strong>Bụng đang đói (${curHunger}%)!</strong> Ngủ sâu sẽ tiêu hao thêm cơn đói, bạn có thể ăn nhẹ ngay bên dưới!
        </div>
      `;
    }

    const sleepHungerLabel =
      bonuses.bedSleepHungerDrain === 0
        ? '0% hao Đói'
        : `${bonuses.bedSleepHungerDrain}% Đói`;

    const closeModal = () => {
      cancelPendingScrollRestores();
      modalContainer.classList.remove('active');
      modalContainer.setAttribute('aria-hidden', 'true');
      modalPortal.innerHTML = '';
    };

    const bedHeaderIcon = renderDecorFurnitureVisual('bed', bonuses.highestBedTier || 1, {
      size: 24,
      idSuffix: 'bed_modal_hdr',
    });
    const fridgeBtnIcon = renderDecorFurnitureVisual('fridge', 1, {
      size: 16,
      idSuffix: 'bed_modal_fridge',
    });

    preserveScrollPositions(
      modalPortal,
      () => {
        modalPortal.innerHTML = `
      <div class="ui-modal-card ui-modal-card--parchment" style="max-width: 364px;">
        <div class="ui-modal-header" style="margin-bottom: 12px;">
          <div>
            <h3 class="ui-modal-title" style="color: #3B2314; display: inline-flex; align-items: center; gap: 7px;">
              <span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:8px;background:#FFFDF7;border:1.8px solid #3B2314;box-shadow:0 1.5px 0 #3B2314;flex-shrink:0;">${bedHeaderIcon}</span>
              <span>Giường Ngủ & Nghỉ Ngơi (T${bonuses.highestBedTier})</span>
            </h3>
            ${
              bonuses.bedWakeUpPayBuff
                ? `<div style="font-size: 10.5px; color: #7C471C; font-weight: 800; margin-top: 4px; display: inline-flex; align-items: center; gap: 4px;">${renderUiGouacheIcon('icon_trophy_medal', { size: 13 })}<span>Đặc quyền T3: Ngủ sâu tặng Buff +15% lương (5 phút)</span></div>`
                : ''
            }
          </div>
          <button type="button" id="modal-close-btn" class="ui-modal-close">✕</button>
        </div>

        <div style="display: flex; gap: 8px; margin-bottom: 12px;">
          <div style="flex: 1; background: #FFFDF7; border: 1.8px solid #3B2314; box-shadow: 0 2px 0 #3B2314; border-radius: 11px; padding: 6px 10px; font-size: 11px; display: flex; align-items: center; justify-content: space-between;">
            <span style="color: #5C3D2E; font-weight: 800; display: inline-flex; align-items: center; gap: 4px;">${renderUiGouacheIcon('icon_energy_bolt', { size: 14 })}<span>Năng lượng:</span></span>
            <strong class="tabular-nums" style="color: ${isTired ? '#B93822' : '#2D6A4F'}; font-weight: 900;">${curEnergy}%</strong>
          </div>
          <div style="flex: 1; background: #FFFDF7; border: 1.8px solid #3B2314; box-shadow: 0 2px 0 #3B2314; border-radius: 11px; padding: 6px 10px; font-size: 11px; display: flex; align-items: center; justify-content: space-between;">
            <span style="color: #5C3D2E; font-weight: 800; display: inline-flex; align-items: center; gap: 4px;">${renderUiGouacheIcon('icon_pantry_platter', { size: 14 })}<span>Độ no:</span></span>
            <strong class="tabular-nums" style="color: ${isHungry ? '#B93822' : '#2D6A4F'}; font-weight: 900;">${curHunger}%</strong>
          </div>
        </div>

        ${statusAlertHtml}

        <div style="display: flex; flex-direction: column; gap: 9px; margin-bottom: 14px;">
          <button type="button" id="bed-rest-btn" style="display: flex; justify-content: space-between; align-items: center; gap: 8px; background: linear-gradient(180deg, #FFF7DB 0%, #F7D993 100%); border: 2px solid #3B2314; box-shadow: 0 3px 0 #3B2314, inset 0 1.5px 0 rgba(255,255,255,0.75); border-radius: 13px; padding: 10px 13px; color: #3B2314; font-weight: 900; cursor: pointer; text-align: left;">
            <span style="display: inline-flex; align-items: center; gap: 6px;">${renderUiGouacheIcon('icon_mood_happy', { size: 17 })}<span>Nằm nghỉ lưng (15p)</span></span>
            <span style="font-size: 10.5px; color: #5C3D2E; font-weight: 800;">+20% Năng lượng • 0 Đói</span>
          </button>
          <button type="button" id="bed-nap-btn" style="display: flex; justify-content: space-between; align-items: center; gap: 8px; background: linear-gradient(180deg, #D5F0DF 0%, #99CEB0 100%); border: 2px solid #3B2314; box-shadow: 0 3px 0 #3B2314, inset 0 1.5px 0 rgba(255,255,255,0.75); border-radius: 13px; padding: 10px 13px; color: #23140C; font-weight: 900; cursor: pointer; text-align: left;">
            <span style="display: inline-flex; align-items: center; gap: 6px;">${renderUiGouacheIcon('icon_energy_bolt', { size: 17 })}<span>Chợp mắt (30 phút)</span></span>
            <span style="font-size: 10.5px; color: #2A3F33; font-weight: 800;">+${bonuses.bedNapEnergyRestore}% NL • +${bonuses.bedNapHappinessBonus}% Vui</span>
          </button>
          <button type="button" id="bed-sleep-btn" style="display: flex; justify-content: space-between; align-items: center; gap: 8px; background: linear-gradient(180deg, #D8ECF8 0%, #9EC9E6 100%); border: 2px solid #3B2314; box-shadow: 0 3px 0 #3B2314, inset 0 1.5px 0 rgba(255,255,255,0.75); border-radius: 13px; padding: 10px 13px; color: #23140C; font-weight: 900; cursor: pointer; text-align: left;">
            <span style="display: inline-flex; align-items: center; gap: 6px;">${renderUiGouacheIcon('icon_time_night', { size: 17 })}<span>Ngủ sâu tới sáng (8:00 AM)</span></span>
            <span style="font-size: 10.5px; color: #2B3E4F; font-weight: 800;">+${bonuses.bedSleepEnergyRestore}% NL • ${sleepHungerLabel}</span>
          </button>
        </div>

        <div style="border-top: 1.5px dashed rgba(59, 35, 20, 0.25); padding-top: 11px;">
          <div style="font-size: 11px; color: #5C3D2E; margin-bottom: 8px; font-weight: 800; display: flex; align-items: center; gap: 5px;">
            ${renderUiGouacheIcon('icon_pantry_platter', { size: 14 })}
            <span>Đang đói bụng khi ở trên giường?</span>
          </div>
          <div style="display: flex; gap: 8px;">
            <button type="button" id="bed-snack-btn" style="flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 5px; background: linear-gradient(180deg, #FFF1BF 0%, #F4C46D 100%); border: 2px solid #3B2314; box-shadow: 0 2.5px 0 #3B2314; border-radius: 11px; padding: 8px 9px; color: #3B2314; font-size: 11px; font-weight: 900; cursor: pointer;">
              ${renderUiGouacheIcon('icon_pantry_platter', { size: 15 })}
              <span>Ăn nhẹ ($15: +25% No)</span>
            </button>
            <button type="button" id="bed-fridge-btn" style="flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 5px; background: linear-gradient(180deg, #D5F0DF 0%, #8CC5A6 100%); border: 2px solid #3B2314; box-shadow: 0 2.5px 0 #3B2314; border-radius: 11px; padding: 8px 9px; color: #23140C; font-size: 11px; font-weight: 900; cursor: pointer;">
              ${fridgeBtnIcon}
              <span>Mở Tủ Lạnh</span>
            </button>
          </div>
        </div>
      </div>
    `;

        modalContainer.classList.add('active');
        modalContainer.setAttribute('aria-hidden', 'false');
        preventButtonFocusScroll(modalPortal);
      },
      { isFreshModalOpen: true }
    );

    document.getElementById('modal-close-btn')?.addEventListener('click', closeModal);

    // 1. Nằm nghỉ lưng (15p): +20% Energy, 0 Hunger drain
    document.getElementById('bed-rest-btn')?.addEventListener('click', () => {
      closeModal();
      GameState.getSurvivalEngine().applyStatDelta({ energy: 20, happiness: 3 });
      GameState.triggerDebouncedSave();
      this.showCharacterStatusBubble('+20% Năng lượng!', 0xd99b66);
      ToastManager.getInstance().show('Đã nằm nghỉ lưng 15 phút! Năng lượng +20% (Không hao Đói)', 'success');
    });

    // 2. Chợp mắt (30p): scaled by Bed Tier
    document.getElementById('bed-nap-btn')?.addEventListener('click', () => {
      closeModal();
      this.cameras.main.fadeOut(300, 0, 0, 0);
      this.time.delayedCall(300, () => {
        GameState.getSurvivalEngine().applyStatDelta({
          energy: bonuses.bedNapEnergyRestore,
          happiness: bonuses.bedNapHappinessBonus,
          hunger: -5
        });
        GameState.triggerDebouncedSave();
        this.showCharacterStatusBubble(`+${bonuses.bedNapEnergyRestore}% Năng lượng!`, 0x81b29a);
        ToastManager.getInstance().show(
          `Đã chợp mắt 30 phút! Năng lượng +${bonuses.bedNapEnergyRestore}%, Tinh thần +${bonuses.bedNapHappinessBonus}%`,
          'success'
        );
        this.cameras.main.fadeIn(300, 0, 0, 0);
      });
    });

    // 3. Ngủ sâu tới sáng: scaled by Bed Tier + Wake-up Pay Buff at T3
    document.getElementById('bed-sleep-btn')?.addEventListener('click', () => {
      closeModal();
      this.cameras.main.fadeOut(400, 0, 0, 0);
      this.time.delayedCall(400, () => {
        const engine = GameState.getSurvivalEngine();
        engine.applyStatDelta({
          energy: bonuses.bedSleepEnergyRestore,
          hunger: bonuses.bedSleepHungerDrain,
          happiness: 12
        });
        if (bonuses.bedWakeUpPayBuff) {
          engine.addBuff({
            id: 'buff_bed_t3_refresh',
            name: 'Giấc Ngủ Hoàng Gia',
            description: '+15% thù lao ca làm việc sau giấc ngủ sâu trên Giường T3',
            type: 'PAY_BOOST',
            magnitude: 0.15,
            expiresAt: Date.now() + 300 * 1000,
            icon: '👑'
          });
        }
        GameState.triggerDebouncedSave();
        this.updateWindowLighting(8);
        EventBus.emit('TIME_ADVANCED', 8);
        this.showCharacterStatusBubble('Sảng khoái tràn đầy!', 0x629e7e);
        ToastManager.getInstance().show(
          bonuses.bedWakeUpPayBuff
            ? 'Giấc ngủ Hoàng Gia! Năng lượng đầy 100% + Buff Sảng Khoái (+15% lương 5 phút)!'
            : `Một giấc ngủ thật ngon! Năng lượng +${bonuses.bedSleepEnergyRestore}%`,
          'success'
        );
        this.cameras.main.fadeIn(400, 0, 0, 0);
      });
    });

    // 4. Ăn nhẹ tại giường ($15 -> +25% Hunger, +10% Energy)
    document.getElementById('bed-snack-btn')?.addEventListener('click', () => {
      if (!GameState.modifyMoney(-15)) {
        ToastManager.getInstance().show('Không đủ $15 để gọi đồ ăn nhẹ! Hãy làm thêm ca hoặc mở Tủ lạnh.', 'warning');
        return;
      }
      closeModal();
      GameState.getSurvivalEngine().applyStatDelta({ hunger: 25, energy: 10, happiness: 5 });
      GameState.triggerDebouncedSave();
      this.showCharacterStatusBubble('+25% No • +10% NL', 0xf59e0b);
      ToastManager.getInstance().show('Đã ăn bữa nhẹ trên giường! Cơn đói +25%, Năng lượng +10%', 'success');
    });

    // 5. Mở Tủ Lạnh / Kho Ẩm Thực
    document.getElementById('bed-fridge-btn')?.addEventListener('click', () => {
      closeModal();
      const placed = GameState.getState().placedFurniture || [];
      const fridgeItem = placed.find((f) => f.typeId === 'fridge');
      if (fridgeItem) {
        this.handleFurnitureTap(fridgeItem);
      } else {
        void import('../ui/Modals/PantryStoreModal').then(({ PantryStoreModal }) => {
          PantryStoreModal.getInstance().open();
        });
      }
    });
  }

  /**
   * Opens Sofa Relaxation Modal restoring Happiness.
   */
  public openSofaModal(): void {
    const modalContainer = document.getElementById('modal-container');
    const modalPortal = document.getElementById('modal-portal');
    if (!modalContainer || !modalPortal) return;

    const bonuses = DecorSystem.getActiveFurnitureBonuses();

    const closeModal = () => {
      cancelPendingScrollRestores();
      modalContainer.classList.remove('active');
      modalContainer.setAttribute('aria-hidden', 'true');
      modalPortal.innerHTML = '';
    };

    const sofaHeaderIcon = renderDecorFurnitureVisual('sofa', bonuses.highestSofaTier || 1, {
      size: 24,
      idSuffix: 'sofa_modal_hdr',
    });

    preserveScrollPositions(
      modalPortal,
      () => {
        modalPortal.innerHTML = `
      <div class="ui-modal-card ui-modal-card--parchment" style="max-width: 364px;">
        <div class="ui-modal-header" style="margin-bottom: 12px;">
          <div>
            <h3 class="ui-modal-title" style="color: #3B2314; display: inline-flex; align-items: center; gap: 7px;">
              <span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:8px;background:#FFFDF7;border:1.8px solid #3B2314;box-shadow:0 1.5px 0 #3B2314;flex-shrink:0;">${sofaHeaderIcon}</span>
              <span>Ghế Sofa Thư Giãn (T${bonuses.highestSofaTier})</span>
            </h3>
            ${
              bonuses.sofaObstacleCostDiscount > 0
                ? `<div style="font-size: 10.5px; color: #2D6A4F; font-weight: 800; margin-top: 4px; display: inline-flex; align-items: center; gap: 4px;">${renderUiGouacheIcon('icon_check_seal', { size: 13 })}<span>Đặc quyền Sofa T${bonuses.highestSofaTier}: Giảm ${Math.round(bonuses.sofaObstacleCostDiscount * 100)}% thiệt hại biến cố xấu</span></div>`
                : ''
            }
          </div>
          <button type="button" id="modal-close-btn" class="ui-modal-close">✕</button>
        </div>
        <p style="font-size: 12px; color: #5C3D2E; font-weight: 600; margin: 0 0 14px; line-height: 1.45;">
          Thư giãn tại phòng giúp nâng cao Tinh thần (%) và giảm căng thẳng sau ca làm việc.
        </p>
        <div style="display: flex; flex-direction: column; gap: 10px;">
          <button type="button" id="sofa-music-btn" style="display: flex; justify-content: space-between; align-items: center; gap: 8px; background: linear-gradient(180deg, #D5F0DF 0%, #99CEB0 100%); border: 2px solid #3B2314; box-shadow: 0 3px 0 #3B2314, inset 0 1.5px 0 rgba(255,255,255,0.75); border-radius: 13px; padding: 11px 14px; color: #23140C; font-weight: 900; cursor: pointer;">
            <span style="display: inline-flex; align-items: center; gap: 6px;">${renderUiGouacheIcon('icon_mood_happy', { size: 17 })}<span>Ngồi nghỉ & Nghe nhạc (15p)</span></span>
            <span style="font-size: 10.5px; color: #2A3F33; font-weight: 800;">+${bonuses.sofaSitHappinessRestore}% Vui • +${bonuses.sofaSitEnergyRestore}% NL</span>
          </button>
          <button type="button" id="sofa-stream-btn" style="display: flex; justify-content: space-between; align-items: center; gap: 8px; background: linear-gradient(180deg, #FAD4C4 0%, #EE9D84 100%); border: 2px solid #3B2314; box-shadow: 0 3px 0 #3B2314, inset 0 1.5px 0 rgba(255,255,255,0.75); border-radius: 13px; padding: 11px 14px; color: #2D160D; font-weight: 900; cursor: pointer;">
            <span style="display: inline-flex; align-items: center; gap: 6px;">${renderUiGouacheIcon('icon_time_sunset', { size: 17 })}<span>Xem phim trực tuyến (30p)</span></span>
            <span style="font-size: 10.5px; color: #4A2518; font-weight: 800;">+${bonuses.sofaTvHappinessRestore}% Tinh thần</span>
          </button>
          <button type="button" id="sofa-game-btn" style="display: flex; justify-content: space-between; align-items: center; gap: 8px; background: linear-gradient(180deg, #FFF1BF 0%, #F4C46D 100%); border: 2px solid #3B2314; box-shadow: 0 3px 0 #3B2314, inset 0 1.5px 0 rgba(255,255,255,0.75); border-radius: 13px; padding: 11px 14px; color: #3B2314; font-weight: 900; cursor: pointer;">
            <span style="display: inline-flex; align-items: center; gap: 6px;">${renderUiGouacheIcon('icon_trophy_medal', { size: 17 })}<span>Chơi game giải trí (1 giờ)</span></span>
            <span style="font-size: 10.5px; color: #5C3D2E; font-weight: 800;">+${bonuses.sofaTvHappinessRestore + 10}% Tinh thần</span>
          </button>
        </div>
      </div>
    `;

        modalContainer.classList.add('active');
        modalContainer.setAttribute('aria-hidden', 'false');
        preventButtonFocusScroll(modalPortal);
      },
      { isFreshModalOpen: true }
    );

    document.getElementById('modal-close-btn')?.addEventListener('click', closeModal);

    document.getElementById('sofa-music-btn')?.addEventListener('click', () => {
      closeModal();
      GameState.getSurvivalEngine().applyStatDelta({
        happiness: bonuses.sofaSitHappinessRestore,
        energy: bonuses.sofaSitEnergyRestore
      });
      GameState.triggerDebouncedSave();
      this.showCharacterStatusBubble(`🎵 +${bonuses.sofaSitHappinessRestore}% Tâm trạng!`, 0x06b6d4);
      ToastManager.getInstance().show(
        `Âm nhạc êm dịu! Tinh thần +${bonuses.sofaSitHappinessRestore}%, Năng lượng +${bonuses.sofaSitEnergyRestore}%`,
        'success'
      );
    });

    document.getElementById('sofa-stream-btn')?.addEventListener('click', () => {
      closeModal();
      GameState.getSurvivalEngine().applyStatDelta({
        happiness: bonuses.sofaTvHappinessRestore,
        hunger: -5
      });
      GameState.triggerDebouncedSave();
      this.showCharacterStatusBubble(`🎬 +${bonuses.sofaTvHappinessRestore}% Tâm trạng!`, 0xa855f7);
      ToastManager.getInstance().show(
        `Đã xem phim! Tinh thần +${bonuses.sofaTvHappinessRestore}%`,
        'success'
      );
    });

    document.getElementById('sofa-game-btn')?.addEventListener('click', () => {
      closeModal();
      const gain = bonuses.sofaTvHappinessRestore + 10;
      GameState.getSurvivalEngine().applyStatDelta({ happiness: gain, energy: -10 });
      GameState.triggerDebouncedSave();
      this.showCharacterStatusBubble(`🎮 +${gain}% Tâm trạng!`, 0xf59e0b);
      ToastManager.getInstance().show(
        `Chiến thắng màn game kịch tính! Tinh thần +${gain}%`,
        'success'
      );
    });
  }

  public getPlayerPos(): GridCoord {
    return { ...this.playerPos };
  }

  public get isWalking(): boolean {
    return this.isMoving;
  }

  public set isWalking(value: boolean) {
    this.isMoving = value;
  }

  public getIsMoving(): boolean {
    return this.isMoving;
  }
}
